/* assets/skin-personality-quiz-v2.js */
/*
 * Skin Personality Quiz V2 (single-file architecture)
 * Section index:
 * 00) File header
 * 01) Static domain constants
 * 02) Copy/data dictionaries
 * 03) Icon registry
 * 04) Pure utility helpers
 * 05) Section runtime
 * 06) Global bootstrap
 *
 * Primary entry points:
 * - initAll(): boot all quiz sections on the page
 * - initSection(root): isolated runtime for a single section instance
 * - buildModel(): derives final report model from answers + map + variants
 * - render(model): paints result UI from the model
 */
/* cspell:ignore nutr horm xmark qids valuenow Spacebar */
(function() {
  "use strict";

  /**
   * @typedef {{ [key: string]: any }} AnyRecord
   * @typedef {{
   *   id?: string;
   *   text?: string;
   *   sub?: string;
   *   primary?: string;
   *   secondary?: string;
   *   intensity?: string;
   *   supplement?: string;
   *   motherhood?: boolean;
   *   ageBand?: number;
   *   axis?: Record<string, number>;
   *   start?: string[];
   *   stop?: string[];
   *   keep?: string[];
   *   [key: string]: any;
   * }} QuestionOption
   * @typedef {{
   *   text?: string;
   *   hint?: string;
   *   options: QuestionOption[];
   *   optionMap?: Record<string, QuestionOption>;
   *   [key: string]: any;
   * }} QuestionConfig
   * @typedef {{
   *   core: AnyRecord[];
   *   morning: AnyRecord[];
   *   evening: AnyRecord[];
   *   night: AnyRecord[];
   *   optional: AnyRecord[];
   * }} RoutineGroups
   * @typedef {{
   *   segment: string;
   *   profileKey: string;
   *   profileTitle: string;
   *   profileSummary: string;
   *   tier: string;
   *   radarScores: Record<string, number>;
   *   strengths: AnyRecord[];
   *   watch: AnyRecord[];
   *   weaknesses: AnyRecord[];
   *   actions: Record<string, string[]>;
   *   insideOutRecommendations: AnyRecord[];
   *   fallbackCollection: string;
   *   hasMap: boolean;
   *   routine: RoutineGroups;
   *   bundleVariantIds: number[];
   *   bundleOutOfStockCount: number;
   *   fullBundleVariantIds?: number[];
   *   [key: string]: any;
   * }} QuizResult
   */

  /* 01) Static domain constants */
  /** @type {string[]} */
  var PROFILE_KEYS = ["oily_combo", "dry_normal", "acne_prone", "anti_aging"];
  /** @type {Record<string, string>} */
  var PROFILE_TO_MAP_TITLE = {
    oily_combo: "Oily & Combination Skin",
    dry_normal: "Dry & Normal Skin",
    acne_prone: "Acne Prone Skin",
    anti_aging: "Anti-Aging Skin",
  };
  /** @type {Record<string, string>} */
  var TIER_BY_INTENSITY = { simple: "essentials", balanced: "balanced", full: "premium" };
  /** @type {string[]} */
  var AXES = ["cleansing", "nutrition", "hormonal_balance", "stress", "moisturizing"];
  /** @type {Record<string, string>} */
  var AXIS_LABELS = {
    cleansing: "Cleansing",
    nutrition: "Nutrition",
    hormonal_balance: "Hormonal balance",
    stress: "Stress resilience",
    moisturizing: "Moisturizing",
  };
  /** @type {Record<string, string[]>} */
  var RADAR_SOURCES = {
    cleansing: ["q08_cleansing_habit", "q03_skin_state"],
    moisturizing: ["q09_moisturizing_consistency", "q03_skin_state"],
    nutrition: ["q10_nutrition_hydration_pattern", "q11_stress_sleep_pattern"],
    stress: ["q11_stress_sleep_pattern", "q03_skin_state"],
    hormonal_balance: ["q06_breakout_flare_pattern", "q11_stress_sleep_pattern"],
  };
  /** @type {string[]} */
  var QUESTION_ORDER = [
    "q01_age_band",
    "q03_skin_state",
    "q04_primary_skin_goal",
    "q06_breakout_flare_pattern",
    "q08_cleansing_habit",
    "q09_moisturizing_consistency",
    "q10_nutrition_hydration_pattern",
    "q11_stress_sleep_pattern",
    "q14_routine_intensity_preference",
  ];

  /* 02) Copy/data dictionaries */
  /** @type {Record<string, { title: string; summary: string; motherSummary: string }>} */
  var PROFILE_META = {
    oily_combo: {
      title: "Oily & Combination Skin",
      summary: "Your skin builds up shine and clogged pores through the day. Doing the same things daily is what fixes it.",
      motherSummary:
        "Your skin is oily or combination. For mothers in their 40s and 50s we aim for oil control you can keep up, and fewer surprise breakouts in busy weeks.",
    },
    dry_normal: {
      title: "Dry & Normal Skin",
      summary: "Your skin does best with steady hydration and a moisturizer that protects it.",
      motherSummary:
        "Your skin is dry or normal. For mothers in their 40s and 50s we protect comfort and repair with a routine that survives a packed day.",
    },
    acne_prone: {
      title: "Acne Prone Skin",
      summary: "Your skin reacts easily and breaks out. Gentle and steady beats strong.",
      motherSummary:
        "Your skin is acne-prone. For mothers in their 40s and 50s we calm the breakouts without making the routine a burden.",
    },
    anti_aging: {
      title: "Anti-Aging Skin",
      summary: "Your skin gains most from daily sun protection and steady repair.",
      motherSummary:
        "Your focus is ageing. For mothers in their 40s and 50s we protect tone and firmness over the long run, not with quick fixes.",
    },
  };

  /** @type {Record<string, string>} */
  var START_ACTIONS = {
    consistent_evening_cleanse: "Wash your face for 60 seconds every night, even when you get in late.",
    barrier_moisture_lock: "Put on a moisturizer every night to seal your skin.",
    hydration_nutrition_support: "For 7 days: water when you wake, at midday and in the evening, plus balanced meals.",
    stress_sleep_reset: "Wind down for 15 minutes before bed, at least 5 nights a week.",
    cycle_pattern_tracking: "Take one minute a day to note where you are in your cycle and how your skin looks.",
    simplify_to_core_steps: "Do the same 3 steps for 14 days before you add anything else.",
    weekly_detox_mask: "Use a detox mask one night a week, two at most, then moisturize.",
    daily_spf_anchor: "Put on SPF every morning, cloudy days included.",
    protected_me_time: "Set aside two 20-minute slots a week for yourself, and keep them.",
    caffeine_cutoff_plan: "Keep coffee to the morning and early afternoon, and drink a glass of water with each one.",
    recovery_evening_anchor: "Take 10 minutes to unwind 4 nights a week: drink water, eat a lighter dinner, switch off.",
  };
  /** @type {Record<string, string>} */
  var STOP_ACTIONS = {
    harsh_scrubbing: "Stop scrubbing hard, using cleansing brushes, and washing with hot water.",
    skipping_evening_cleanse: "Stop skipping your night cleanse, above all after SPF or makeup.",
    product_hopping: "Stop switching products before you have given them 3 to 4 weeks.",
    over_exfoliating: "Stop putting several strong treatment or exfoliation nights in the same week.",
    sleeping_late_consistently: "Stop treating short sleep as normal.",
    under_hydrating: "Stop going hours without water. Set a reminder if it helps.",
    no_pattern_tracking: "Stop guessing when hormonal breakouts will hit. Write down your cycle, stress and sleep.",
    late_caffeine_spikes: "Stop drinking coffee after mid-afternoon so you sleep deeply and your skin repairs overnight.",
    late_alcohol_sugar_cycle: "Stop the regular late nights of alcohol and sugar. They dry you out and show the next day.",
  };
  /** @type {Record<string, string>} */
  var KEEP_ACTIONS = {
    daily_spf_anchor: "Keep SPF as a fixed part of your morning.",
    steady_basic_routine: "Keep your core routine the same for at least 4 weeks.",
    gentle_consistent_cleansing: "Keep cleansing gentle, short and regular.",
    hydration_habit: "Keep drinking water steadily through the day.",
    stress_management_habit: "Keep doing whatever already brings your stress down.",
    balanced_food_habit: "Keep meals built on protein, fibre and plenty of water.",
    treatment_boundaries: "Keep treatments spaced out. More is not better.",
    caffeine_boundary_habit: "Keep coffee to the same times, mostly early in the day.",
    evening_recovery_rhythm: "Keep evenings for recovery: water, a lighter dinner and little alcohol.",
  };
  /** @type {Record<string, string[]>} */
  var ACTION_TAG_AXES = {
    consistent_evening_cleanse: ["cleansing"],
    barrier_moisture_lock: ["moisturizing"],
    hydration_nutrition_support: ["nutrition", "moisturizing"],
    stress_sleep_reset: ["stress", "hormonal_balance"],
    cycle_pattern_tracking: ["hormonal_balance"],
    simplify_to_core_steps: ["cleansing", "moisturizing", "stress"],
    weekly_detox_mask: ["cleansing"],
    daily_spf_anchor: ["moisturizing"],
    protected_me_time: ["stress"],
    caffeine_cutoff_plan: ["nutrition", "stress", "hormonal_balance"],
    recovery_evening_anchor: ["nutrition", "stress", "hormonal_balance", "moisturizing"],
    harsh_scrubbing: ["cleansing", "moisturizing"],
    skipping_evening_cleanse: ["cleansing"],
    product_hopping: ["cleansing", "moisturizing", "stress"],
    over_exfoliating: ["cleansing", "moisturizing"],
    sleeping_late_consistently: ["stress", "hormonal_balance"],
    under_hydrating: ["nutrition", "moisturizing"],
    no_pattern_tracking: ["hormonal_balance"],
    late_caffeine_spikes: ["nutrition", "stress", "hormonal_balance"],
    late_alcohol_sugar_cycle: ["nutrition", "hormonal_balance", "moisturizing"],
    gentle_consistent_cleansing: ["cleansing"],
    steady_basic_routine: ["moisturizing", "stress"],
    hydration_habit: ["nutrition", "moisturizing"],
    stress_management_habit: ["stress"],
    balanced_food_habit: ["nutrition"],
    treatment_boundaries: ["hormonal_balance", "cleansing"],
    caffeine_boundary_habit: ["nutrition", "stress"],
    evening_recovery_rhythm: ["nutrition", "stress", "hormonal_balance", "moisturizing"],
  };
  /** @type {Record<string, { weakness: string[]; watch: string[]; strength: string[] }>} */
  var AXIS_CHANGE_KEYS = {
    cleansing: {
      weakness: ["consistent_evening_cleanse", "harsh_scrubbing", "skipping_evening_cleanse"],
      watch: ["simplify_to_core_steps", "product_hopping", "gentle_consistent_cleansing"],
      strength: ["gentle_consistent_cleansing", "treatment_boundaries", "daily_spf_anchor"],
    },
    nutrition: {
      weakness: ["hydration_nutrition_support", "under_hydrating", "balanced_food_habit"],
      watch: ["hydration_nutrition_support", "balanced_food_habit", "hydration_habit"],
      strength: ["balanced_food_habit", "hydration_habit", "daily_spf_anchor"],
    },
    hormonal_balance: {
      weakness: ["cycle_pattern_tracking", "no_pattern_tracking", "stress_sleep_reset"],
      watch: ["cycle_pattern_tracking", "stress_sleep_reset", "treatment_boundaries"],
      strength: ["treatment_boundaries", "stress_management_habit", "cycle_pattern_tracking"],
    },
    stress: {
      weakness: ["stress_sleep_reset", "sleeping_late_consistently", "protected_me_time"],
      watch: ["stress_sleep_reset", "protected_me_time", "stress_management_habit"],
      strength: ["stress_management_habit", "steady_basic_routine", "protected_me_time"],
    },
    moisturizing: {
      weakness: ["barrier_moisture_lock", "over_exfoliating", "harsh_scrubbing"],
      watch: ["simplify_to_core_steps", "barrier_moisture_lock", "steady_basic_routine"],
      strength: ["steady_basic_routine", "hydration_habit", "daily_spf_anchor"],
    },
  };

  /** @type {Record<string, Record<string, string>>} */
  var AXIS_NOTES = {
    cleansing: {
      strength: "Your cleansing is working ({score}/100). You get your skin clean without stripping it.",
      weakness: "Cleansing is your weakest point ({score}/100). Washing too hard, or skipping it at night, keeps your skin unsettled.",
      watch: "Your cleansing is half there ({score}/100). The habit is good, but it slips on busy days.",
    },
    nutrition: {
      strength: "You eat and drink well for your skin ({score}/100). That gives it what it needs to repair.",
      weakness: "Food and water are holding your skin back ({score}/100). Eating and drinking at random times slows repair.",
      watch: "Your food and water habits are decent ({score}/100). Not steady enough yet to carry you through a hard week.",
    },
    hormonal_balance: {
      strength: "Your hormones are steady right now ({score}/100). Breakouts come when you expect them, not out of nowhere.",
      weakness: "Hormones are driving your skin ({score}/100). Breakouts catch you by surprise because nothing is written down.",
      watch: "Your hormones are partly under control ({score}/100). Note when breakouts land and what set them off.",
    },
    stress: {
      strength: "Stress is not reaching your skin ({score}/100). The way you rest is protecting it.",
      weakness: "Stress is showing on your skin ({score}/100). Short nights and heavy weeks feed redness and breakouts.",
      watch: "Stress sits in the middle ({score}/100). More regular rest will steady your skin.",
    },
    moisturizing: {
      strength: "Your skin holds moisture well ({score}/100). It feels comfortable from one day to the next.",
      weakness: "Your skin is losing moisture ({score}/100). It turns tight and rough because nothing seals it in.",
      watch: "Moisture is halfway there ({score}/100). The basics are in place; using them daily is what is missing.",
    },
  };

  /*
   * Spanish copy for the quiz questions, report narrative, and product roles.
   * Store convention (see assets/es-fallback-translate.js): correctly accented Spanish
   * with inverted question marks, matching the accented Spanish in locales/es.json.
   * Keep it plain and short: say the thing, cut the words that only pad it.
   * Keys must match the English source strings exactly.
   */
  /** @type {Record<string, string>} */
  var ES_STRINGS = {
    /* q01 age band */
    "What's your age?": "¿Cuál es tu edad?",
    "We use it to shape your routine.": "Nos sirve para ajustar tu rutina.",
    "Under 30": "Menos de 30",
    "30 to 39": "30 a 39",
    "40 to 49": "40 a 49",
    "50+": "50 o más",
    /* q03 skin state (merged) */
    "How does your skin feel during the day?": "¿Cómo sientes la piel durante el día?",
    "Shiny and oily by midday": "Brillante y grasa al mediodía",
    "Shine and clogged pores by evening": "Brillo y poros tapados por la tarde",
    "Tight, dry, or dehydrated": "Tirante, seca o deshidratada",
    "Tight and rough, mainly on the cheeks": "Tirante y áspera, sobre todo en las mejillas",
    "Reactive, red, or breakout-prone": "Reactiva, roja o con brotes",
    "Easily irritated or breaking out": "Se irrita o le salen granitos",
    "Dull, tired, or less firm": "Apagada, cansada o menos firme",
    "Missing its usual glow": "Sin su brillo de siempre",
    "Mostly comfortable and balanced": "Cómoda y equilibrada",
    "No complaints, keeping it that way": "Sin quejas, quiero que siga así",
    /* q04 primary goal */
    "What's your main goal?": "¿Cuál es tu objetivo?",
    "Less shine and clearer pores": "Menos brillo y poros más limpios",
    "Control oil, pores and blackheads": "Controlar grasa, poros y puntos negros",
    "More comfort and hydration": "Más confort e hidratación",
    "Soft, plump, never tight": "Suave, jugosa, sin tirantez",
    "Fewer breakouts": "Menos brotes",
    "Fewer breakouts, less redness": "Menos brotes y menos rojeces",
    "More firmness and glow": "Más firmeza y brillo",
    "Soften fine lines, restore bounce": "Suavizar líneas finas y recuperar elasticidad",
    /* q06 flare pattern */
    "When does your skin flare up?": "¿Cuándo reacciona tu piel?",
    "With my cycle or hormones": "Con mi ciclo o las hormonas",
    "Before my period": "Antes de la regla",
    "With stress and bad sleep": "Con estrés y mal sueño",
    "After changing products": "Al cambiar de productos",
    "Rarely, mostly stable": "Rara vez, casi siempre estable",
    /* q08 cleansing habit */
    "How do you cleanse?": "¿Cómo te limpias la cara?",
    "Gently, morning and night": "Con suavidad, mañana y noche",
    "I often skip it at night": "Muchas noches me la salto",
    "I scrub hard to feel clean": "Froto fuerte para sentirla limpia",
    "It depends on the day": "Depende del día",
    /* q09 moisturizing consistency */
    "How often do you moisturize?": "¿Cada cuánto te hidratas?",
    "Consistency beats product count.": "La constancia importa más que el número de productos.",
    "Morning and night": "Mañana y noche",
    "Once a day, usually at night": "Una vez al día, casi siempre de noche",
    "Only when I feel dry": "Solo cuando la noto seca",
    "Rarely or never": "Rara vez o nunca",
    /* q10 nutrition/hydration */
    "How do you eat and drink in a normal week?": "¿Cómo comes y bebes en una semana normal?",
    "What you eat and drink shows on your skin.": "Lo que comes y bebes se nota en tu piel.",
    "Steady meals and water most days": "Comidas y agua estables casi a diario",
    "Busy days: skipped meals, little water": "Días ocupados: me salto comidas y bebo poca agua",
    "Some good days, some bad": "Unos días bien, otros mal",
    "Mostly fast, processed food": "Sobre todo comida rápida y procesada",
    /* q11 stress/sleep */
    "How are your stress and sleep?": "¿Cómo están tu estrés y tu sueño?",
    "Calm, sleeping enough": "Tranquila y durmiendo bien",
    "High stress, little sleep": "Mucho estrés y poco sueño",
    "Stressed, but sleeping fine": "Con estrés, pero duermo bien",
    "Calm, but sleeping badly": "Tranquila, pero duermo mal",
    /* q14 routine completeness */
    "How complete do you want your routine?": "¿Qué tan completa quieres tu rutina?",
    "You can change this anytime.": "Puedes cambiarlo cuando quieras.",
    "Essentials only": "Solo lo esencial",
    "Three steps you'll actually keep up": "Tres pasos que sí vas a mantener",
    "Balanced routine": "Rutina equilibrada",
    "Daily basics plus a few extras": "Lo básico diario y algún extra",
    "Complete program": "Programa completo",
    "Morning, night and weekly treatments": "Mañana, noche y tratamientos semanales",
    /* Profiles */
    "Oily & Combination Skin": "Piel grasa y mixta",
    "Your skin builds up shine and clogged pores through the day. Doing the same things daily is what fixes it.": "Tu piel acumula brillo y poros tapados durante el día. Lo que lo arregla es repetir lo mismo a diario.",
    "Dry & Normal Skin": "Piel seca y normal",
    "Your skin does best with steady hydration and a moisturizer that protects it.": "Tu piel va mejor con hidratación constante y una crema que la proteja.",
    "Acne Prone Skin": "Piel con tendencia al acné",
    "Your skin reacts easily and breaks out. Gentle and steady beats strong.": "Tu piel reacciona rápido y le salen brotes. Suave y constante gana a fuerte.",
    "Anti-Aging Skin": "Piel antiedad",
    "Your skin gains most from daily sun protection and steady repair.": "Tu piel gana sobre todo con protección solar diaria y reparación constante.",
    /* Profile summaries for the 40-55 segment */
    "Your skin is oily or combination. For mothers in their 40s and 50s we aim for oil control you can keep up, and fewer surprise breakouts in busy weeks.": "Tu piel es grasa o mixta. Para madres de 40 y 50 buscamos un control de grasa que puedas mantener y menos brotes por sorpresa en semanas ocupadas.",
    "Your skin is dry or normal. For mothers in their 40s and 50s we protect comfort and repair with a routine that survives a packed day.": "Tu piel es seca o normal. Para madres de 40 y 50 protegemos el confort y la reparación con una rutina que aguanta un día lleno.",
    "Your skin is acne-prone. For mothers in their 40s and 50s we calm the breakouts without making the routine a burden.": "Tu piel tiene tendencia al acné. Para madres de 40 y 50 calmamos los brotes sin que la rutina sea una carga.",
    "Your focus is ageing. For mothers in their 40s and 50s we protect tone and firmness over the long run, not with quick fixes.": "Tu foco es la edad. Para madres de 40 y 50 protegemos el tono y la firmeza a largo plazo, no con soluciones rápidas.",
    /* Axis labels + statuses */
    "Cleansing": "Limpieza",
    "Nutrition": "Nutrición",
    "Hormonal balance": "Balance hormonal",
    "Stress resilience": "Estrés",
    "Moisturizing": "Hidratación",
    "Strong": "Fuerte",
    "Priority fix": "A corregir",
    "Needs consistency": "Necesita constancia",
    "this area": "esta área",
    "Show {axis} score breakdown": "Ver el desglose de {axis}",
    /* Start actions */
    "Wash your face for 60 seconds every night, even when you get in late.": "Lávate la cara 60 segundos cada noche, aunque llegues tarde.",
    "Put on a moisturizer every night to seal your skin.": "Ponte crema hidratante cada noche para sellar la piel.",
    "For 7 days: water when you wake, at midday and in the evening, plus balanced meals.": "Durante 7 días: agua al despertar, al mediodía y por la noche, y comidas equilibradas.",
    "Wind down for 15 minutes before bed, at least 5 nights a week.": "Desconecta 15 minutos antes de dormir, al menos 5 noches por semana.",
    "Take one minute a day to note where you are in your cycle and how your skin looks.": "Dedica un minuto al día a anotar en qué punto del ciclo estás y cómo ves tu piel.",
    "Do the same 3 steps for 14 days before you add anything else.": "Haz los mismos 3 pasos durante 14 días antes de añadir nada más.",
    "Use a detox mask one night a week, two at most, then moisturize.": "Usa una mascarilla detox una noche por semana, dos como máximo, y luego hidrata.",
    "Put on SPF every morning, cloudy days included.": "Ponte SPF cada mañana, también los días nublados.",
    "Set aside two 20-minute slots a week for yourself, and keep them.": "Reserva dos ratos de 20 minutos por semana para ti, y cúmplelos.",
    "Keep coffee to the morning and early afternoon, and drink a glass of water with each one.": "Toma café solo por la mañana y primera hora de la tarde, con un vaso de agua cada vez.",
    "Take 10 minutes to unwind 4 nights a week: drink water, eat a lighter dinner, switch off.": "Dedica 10 minutos a desconectar 4 noches por semana: bebe agua, cena ligero y apaga pantallas.",
    /* Stop actions */
    "Stop scrubbing hard, using cleansing brushes, and washing with hot water.": "Deja de frotar fuerte, de usar cepillos y de lavarte con agua caliente.",
    "Stop skipping your night cleanse, above all after SPF or makeup.": "Deja de saltarte la limpieza de noche, sobre todo con SPF o maquillaje.",
    "Stop switching products before you have given them 3 to 4 weeks.": "Deja de cambiar de productos antes de darles 3 o 4 semanas.",
    "Stop putting several strong treatment or exfoliation nights in the same week.": "Deja de juntar varias noches de tratamiento fuerte o exfoliación en la misma semana.",
    "Stop treating short sleep as normal.": "Deja de dar por normal dormir poco.",
    "Stop going hours without water. Set a reminder if it helps.": "Deja de pasar horas sin beber agua. Pon una alarma si te ayuda.",
    "Stop guessing when hormonal breakouts will hit. Write down your cycle, stress and sleep.": "Deja de adivinar cuándo llegan los brotes hormonales. Apunta tu ciclo, tu estrés y tu sueño.",
    "Stop drinking coffee after mid-afternoon so you sleep deeply and your skin repairs overnight.": "Deja el café a partir de media tarde para dormir profundo y que tu piel se repare de noche.",
    "Stop the regular late nights of alcohol and sugar. They dry you out and show the next day.": "Deja las noches seguidas de alcohol y azúcar. Te deshidratan y se notan al día siguiente.",
    /* Keep actions */
    "Keep SPF as a fixed part of your morning.": "Mantén el SPF como parte fija de tu mañana.",
    "Keep your core routine the same for at least 4 weeks.": "Mantén tu rutina base igual durante al menos 4 semanas.",
    "Keep cleansing gentle, short and regular.": "Mantén una limpieza suave, corta y regular.",
    "Keep drinking water steadily through the day.": "Mantén el agua repartida a lo largo del día.",
    "Keep doing whatever already brings your stress down.": "Mantén lo que ya te baja el estrés.",
    "Keep meals built on protein, fibre and plenty of water.": "Mantén comidas con proteína, fibra y bastante agua.",
    "Keep treatments spaced out. More is not better.": "Mantén los tratamientos espaciados. Más no es mejor.",
    "Keep coffee to the same times, mostly early in the day.": "Mantén el café a las mismas horas, sobre todo temprano.",
    "Keep evenings for recovery: water, a lighter dinner and little alcohol.": "Mantén las noches para recuperarte: agua, cena ligera y poco alcohol.",
    /* Axis notes (templated) */
    "Your cleansing is working ({score}/100). You get your skin clean without stripping it.": "Tu limpieza funciona ({score}/100). Dejas la piel limpia sin resecarla.",
    "Cleansing is your weakest point ({score}/100). Washing too hard, or skipping it at night, keeps your skin unsettled.": "La limpieza es tu punto más débil ({score}/100). Frotar demasiado, o saltártela de noche, mantiene la piel alterada.",
    "Your cleansing is half there ({score}/100). The habit is good, but it slips on busy days.": "Tu limpieza está a medias ({score}/100). El hábito es bueno, pero se te escapa los días ocupados.",
    "You eat and drink well for your skin ({score}/100). That gives it what it needs to repair.": "Comes y bebes bien para tu piel ({score}/100). Así tiene con qué repararse.",
    "Food and water are holding your skin back ({score}/100). Eating and drinking at random times slows repair.": "La comida y el agua frenan tu piel ({score}/100). Comer y beber a horas sueltas retrasa la reparación.",
    "Your food and water habits are decent ({score}/100). Not steady enough yet to carry you through a hard week.": "Tus hábitos de comida y agua están bien ({score}/100). Aún no bastan para una semana dura.",
    "Your hormones are steady right now ({score}/100). Breakouts come when you expect them, not out of nowhere.": "Tus hormonas están estables ({score}/100). Los brotes llegan cuando los esperas, no de la nada.",
    "Hormones are driving your skin ({score}/100). Breakouts catch you by surprise because nothing is written down.": "Las hormonas mandan en tu piel ({score}/100). Los brotes te sorprenden porque no llevas registro.",
    "Your hormones are partly under control ({score}/100). Note when breakouts land and what set them off.": "Tus hormonas están medio controladas ({score}/100). Apunta cuándo llegan los brotes y qué los provocó.",
    "Stress is not reaching your skin ({score}/100). The way you rest is protecting it.": "El estrés no llega a tu piel ({score}/100). Tu forma de descansar la protege.",
    "Stress is showing on your skin ({score}/100). Short nights and heavy weeks feed redness and breakouts.": "El estrés se te nota en la piel ({score}/100). Dormir poco y las semanas duras alimentan rojeces y brotes.",
    "Stress sits in the middle ({score}/100). More regular rest will steady your skin.": "El estrés está a medio camino ({score}/100). Descansar con más regularidad calmará tu piel.",
    "Your skin holds moisture well ({score}/100). It feels comfortable from one day to the next.": "Tu piel retiene bien la hidratación ({score}/100). Se siente cómoda de un día para otro.",
    "Your skin is losing moisture ({score}/100). It turns tight and rough because nothing seals it in.": "Tu piel pierde hidratación ({score}/100). Se pone tirante y áspera porque nada la sella.",
    "Moisture is halfway there ({score}/100). The basics are in place; using them daily is what is missing.": "La hidratación está a medias ({score}/100). Tienes lo básico; falta usarlo a diario.",
    /* Axis fallbacks (templated) */
    "What you already do for {area} is keeping your skin steady.": "Lo que ya haces con {area} mantiene tu piel estable.",
    "Your {area} habits help, but they are not there every week yet.": "Tus hábitos de {area} ayudan, pero aún no están todas las semanas.",
    "You have made a start on {area}, but the gaps still outweigh it.": "Has empezado con {area}, pero aún pesan más los fallos.",
    "The main risk is changing too much at once.": "El riesgo principal es cambiar demasiado a la vez.",
    "Skipping {area} is costing you most of what your routine could do.": "Saltarte {area} te quita casi todo lo que tu rutina podría dar.",
    "Rarely doing {area} is feeding your breakouts and slowing recovery.": "Hacer {area} pocas veces alimenta tus brotes y frena la recuperación.",
    "Keep it simple for 14 days, then check this score again.": "Mantenlo simple 14 días y vuelve a mirar esta puntuación.",
    /* Product card roles (from spq-product-map.json) */
    "Cleanse": "Limpieza",
    "Moisturize": "Hidratación",
    "Protect": "Protección",
    "Balance": "Equilibrio",
    "Treat": "Tratamiento",
    "Soothe": "Calmar",
    "Reset, 1 to 2x weekly": "Reinicio, 1 a 2 veces por semana",
    "Reset, 1x weekly": "Reinicio, 1 vez por semana",
    "Texture support, 1 to 2x weekly": "Textura, 1 a 2 veces por semana",
    "Texture control, 1x weekly": "Control de textura, 1 vez por semana",
    "Treat, 1 to 2x weekly": "Tratamiento, 1 a 2 veces por semana",
    "Treat, 2x weekly": "Tratamiento, 2 veces por semana",
    "Support, 1x weekly": "Apoyo, 1 vez por semana",
    "Support, 1 to 2x weekly": "Apoyo, 1 a 2 veces por semana",
    "Support, 2x weekly (AM or PM)": "Apoyo, 2 veces por semana (día o noche)",
    "Support, 2 to 3x weekly (AM or PM)": "Apoyo, 2 a 3 veces por semana (día o noche)",
    "Night support, 2 to 3x weekly": "Apoyo nocturno, 2 a 3 veces por semana",
    "Night support, 2 to 4x weekly": "Apoyo nocturno, 2 a 4 veces por semana",
    "Night support, 3 to 4x weekly": "Apoyo nocturno, 3 a 4 veces por semana",
    "Night support, 4x weekly": "Apoyo nocturno, 4 veces por semana",
    "Higher protection option": "Más protección",
    "Hydration support, 1 to 2x weekly": "Hidratación, 1 a 2 veces por semana",
    "Travel routine": "Rutina de viaje",
    "Recovery, 2x weekly": "Recuperación, 2 veces por semana",
    "Recovery support": "Recuperación",
    "Calm spikes, 1x weekly": "Calmar picos, 1 vez por semana",
    "Calm, 1x weekly": "Calmar, 1 vez por semana",
    "Protect + even tone": "Protección y tono uniforme",
    "Optional, coverage": "Opcional, cobertura",
    "Optional, one step AM": "Opcional, un paso de día",
    "Inside-out support (optional)": "Apoyo interno (opcional)",
    "Optional support": "Apoyo opcional",
    "Recommended alternative": "Alternativa recomendada",
  };

  /** @type {Record<string, QuestionConfig>} */
  var QUESTIONS = {
    q01_age_band: {
      text: "What's your age?",
      hint: "We use it to shape your routine.",
      options: [
        { id: "u30", text: "Under 30", ageBand: 25 },
        { id: "a30_39", text: "30 to 39", ageBand: 35 },
        { id: "a40_49", text: "40 to 49", ageBand: 44 },
        { id: "a50p", text: "50+", ageBand: 55 },
      ],
    },
    q03_skin_state: {
      text: "How does your skin feel during the day?",
      options: [
        {
          id: "state_oily",
          text: "Shiny and oily by midday",
          sub: "Shine and clogged pores by evening",
          primary: "oily_combo",
          secondary: "acne_prone",
          axis: { cleansing: 1, moisturizing: 2, stress: 2 },
          start: ["consistent_evening_cleanse", "weekly_detox_mask"],
          stop: ["skipping_evening_cleanse"],
        },
        {
          id: "state_tight",
          text: "Tight, dry, or dehydrated",
          sub: "Tight and rough, mainly on the cheeks",
          primary: "dry_normal",
          secondary: "anti_aging",
          axis: { cleansing: 2, moisturizing: 1, stress: 2 },
          start: ["barrier_moisture_lock"],
          stop: ["harsh_scrubbing"],
        },
        {
          id: "state_reactive",
          text: "Reactive, red, or breakout-prone",
          sub: "Easily irritated or breaking out",
          primary: "acne_prone",
          secondary: "dry_normal",
          axis: { cleansing: 2, moisturizing: 2, stress: 1 },
          start: ["simplify_to_core_steps"],
          stop: ["over_exfoliating"],
        },
        {
          id: "state_dull",
          text: "Dull, tired, or less firm",
          sub: "Missing its usual glow",
          primary: "anti_aging",
          secondary: "dry_normal",
          axis: { cleansing: 3, moisturizing: 2, stress: 2 },
          keep: ["daily_spf_anchor"],
        },
        {
          id: "state_balanced",
          text: "Mostly comfortable and balanced",
          sub: "No complaints, keeping it that way",
          primary: "dry_normal",
          axis: { cleansing: 3, moisturizing: 3, stress: 3 },
          keep: ["steady_basic_routine"],
        },
      ],
    },
    q04_primary_skin_goal: {
      text: "What's your main goal?",
      options: [
        { id: "goal_clear", text: "Less shine and clearer pores", sub: "Control oil, pores and blackheads", primary: "oily_combo", secondary: "acne_prone" },
        { id: "goal_comfort", text: "More comfort and hydration", sub: "Soft, plump, never tight", primary: "dry_normal", secondary: "anti_aging" },
        { id: "goal_flares", text: "Fewer breakouts", sub: "Fewer breakouts, less redness", primary: "acne_prone", secondary: "oily_combo" },
        { id: "goal_firm", text: "More firmness and glow", sub: "Soften fine lines, restore bounce", primary: "anti_aging", secondary: "dry_normal" },
      ],
    },
    q06_breakout_flare_pattern: {
      text: "When does your skin flare up?",
      options: [
        {
          id: "flare_cycle",
          text: "With my cycle or hormones",
          sub: "Before my period",
          primary: "acne_prone",
          secondary: "oily_combo",
          axis: { hormonal_balance: 1 },
          start: ["cycle_pattern_tracking"],
          stop: ["no_pattern_tracking"],
        },
        {
          id: "flare_stress",
          text: "With stress and bad sleep",
          primary: "acne_prone",
          secondary: "anti_aging",
          axis: { hormonal_balance: 2 },
          start: ["stress_sleep_reset"],
          stop: ["sleeping_late_consistently"],
        },
        {
          id: "flare_switching",
          text: "After changing products",
          primary: "dry_normal",
          secondary: "acne_prone",
          axis: { hormonal_balance: 2 },
          stop: ["product_hopping"],
          start: ["simplify_to_core_steps"],
        },
        {
          id: "flare_rare",
          text: "Rarely, mostly stable",
          axis: { hormonal_balance: 4 },
          keep: ["treatment_boundaries"],
        },
      ],
    },
    q08_cleansing_habit: {
      text: "How do you cleanse?",
      options: [
        {
          id: "cleanse_consistent",
          text: "Gently, morning and night",
          axis: { cleansing: 4 },
          keep: ["gentle_consistent_cleansing"],
        },
        {
          id: "cleanse_skip",
          text: "I often skip it at night",
          axis: { cleansing: 1 },
          stop: ["skipping_evening_cleanse"],
          start: ["consistent_evening_cleanse"],
        },
        {
          id: "cleanse_aggressive",
          text: "I scrub hard to feel clean",
          axis: { cleansing: 1 },
          stop: ["harsh_scrubbing", "over_exfoliating"],
        },
        {
          id: "cleanse_inconsistent",
          text: "It depends on the day",
          axis: { cleansing: 2 },
          start: ["simplify_to_core_steps", "protected_me_time"],
        },
      ],
    },
    q09_moisturizing_consistency: {
      text: "How often do you moisturize?",
      hint: "Consistency beats product count.",
      options: [
        {
          id: "moist_twice",
          text: "Morning and night",
          axis: { moisturizing: 4 },
          keep: ["steady_basic_routine"],
        },
        {
          id: "moist_once",
          text: "Once a day, usually at night",
          axis: { moisturizing: 3 },
        },
        {
          id: "moist_if_dry",
          text: "Only when I feel dry",
          axis: { moisturizing: 2 },
          start: ["barrier_moisture_lock"],
        },
        {
          id: "moist_rare",
          text: "Rarely or never",
          axis: { moisturizing: 1 },
          start: ["barrier_moisture_lock"],
        },
      ],
    },
    q10_nutrition_hydration_pattern: {
      text: "How do you eat and drink in a normal week?",
      hint: "What you eat and drink shows on your skin.",
      options: [
        {
          id: "nutr_good",
          text: "Steady meals and water most days",
          axis: { nutrition: 4 },
          keep: ["balanced_food_habit", "hydration_habit"],
        },
        {
          id: "nutr_busy",
          text: "Busy days: skipped meals, little water",
          axis: { nutrition: 1 },
          start: ["hydration_nutrition_support"],
          stop: ["under_hydrating"],
        },
        {
          id: "nutr_mixed",
          text: "Some good days, some bad",
          axis: { nutrition: 2 },
          start: ["hydration_nutrition_support"],
        },
        {
          id: "nutr_processed",
          text: "Mostly fast, processed food",
          axis: { nutrition: 1 },
          start: ["hydration_nutrition_support"],
        },
      ],
    },
    q11_stress_sleep_pattern: {
      text: "How are your stress and sleep?",
      options: [
        {
          id: "stress_low",
          text: "Calm, sleeping enough",
          axis: { nutrition: 3, stress: 4, hormonal_balance: 4 },
          keep: ["stress_management_habit"],
        },
        {
          id: "stress_high_sleep_bad",
          text: "High stress, little sleep",
          axis: { nutrition: 1, stress: 1, hormonal_balance: 1 },
          start: ["stress_sleep_reset"],
          stop: ["sleeping_late_consistently"],
        },
        {
          id: "stress_high_sleep_ok",
          text: "Stressed, but sleeping fine",
          axis: { nutrition: 2, stress: 2, hormonal_balance: 2 },
          start: ["protected_me_time"],
        },
        {
          id: "stress_ok_sleep_bad",
          text: "Calm, but sleeping badly",
          axis: { nutrition: 2, stress: 2, hormonal_balance: 2 },
          start: ["stress_sleep_reset"],
        },
      ],
    },
    q14_routine_intensity_preference: {
      text: "How complete do you want your routine?",
      hint: "You can change this anytime.",
      options: [
        {
          id: "int_simple",
          text: "Essentials only",
          sub: "Three steps you'll actually keep up",
          intensity: "simple",
        },
        {
          id: "int_balanced",
          text: "Balanced routine",
          sub: "Daily basics plus a few extras",
          intensity: "balanced",
        },
        {
          id: "int_full",
          text: "Complete program",
          sub: "Morning, night and weekly treatments",
          intensity: "full",
        },
      ],
    },
  };
  /* 03) Icon registry */
  /* Heroicons (outline, 24x24) mapped to answer concepts */
  /** @type {Record<string, string>} */
  var SPQ_ICONS = {
    sun: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"/></svg>',
    drop: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m20.893 13.393-1.135-1.135a2.252 2.252 0 0 1-.421-.585l-1.08-2.16a.414.414 0 0 0-.663-.107.827.827 0 0 1-.812.21l-1.273-.363a.89.89 0 0 0-.738 1.595l.587.39c.59.395.674 1.23.172 1.732l-.2.2c-.212.212-.33.498-.33.796v.41c0 .409-.11.809-.32 1.158l-1.315 2.191a2.11 2.11 0 0 1-1.81 1.025 1.055 1.055 0 0 1-1.055-1.055v-1.172c0-.92-.56-1.747-1.414-2.089l-.655-.261a2.25 2.25 0 0 1-1.383-2.46l.007-.042a2.25 2.25 0 0 1 .29-.787l.09-.15a2.25 2.25 0 0 1 2.37-1.048l1.178.236a1.125 1.125 0 0 0 1.302-.795l.208-.73a1.125 1.125 0 0 0-.578-1.315l-.665-.332-.091.091a2.25 2.25 0 0 1-1.591.659h-.18a.94.94 0 0 0-.662.274.931.931 0 0 1-1.458-1.137l1.411-2.353a2.25 2.25 0 0 0 .286-.76m11.928 9.869A9 9 0 0 0 8.965 3.525m11.928 9.868A9 9 0 1 1 8.965 3.525"/></svg>',
    fire: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.362 5.214A8.252 8.252 0 0 1 12 21 8.25 8.25 0 0 1 6.038 7.047 8.287 8.287 0 0 0 9 9.601a8.983 8.983 0 0 1 3.361-6.867 8.21 8.21 0 0 0 3 2.48Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M12 18a3.75 3.75 0 0 0 .495-7.468 5.99 5.99 0 0 0-1.925 3.547 5.975 5.975 0 0 1-2.133-1.001A3.75 3.75 0 0 0 12 18Z"/></svg>',
    sparkles: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"/></svg>',
    exclamation: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>',
    heart: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"/></svg>',
    bolt: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z"/></svg>',
    scale: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0 0 12 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 0 1-2.031.352 5.988 5.988 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971Zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 0 1-2.031.352 5.989 5.989 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971Z"/></svg>',
    stack: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3"/></svg>',
    hand: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M10.05 4.575a1.575 1.575 0 1 0-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 0 1 3.15 0v1.5m-3.15 0 .075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 0 1 3.15 0V15M6.9 7.575a1.575 1.575 0 1 0-3.15 0v8.175a6.75 6.75 0 0 0 6.75 6.75h2.018a5.25 5.25 0 0 0 3.712-1.538l1.732-1.732a5.25 5.25 0 0 0 1.538-3.712l.003-2.024a.668.668 0 0 0-.668-.668 1.336 1.336 0 0 0-1.336 1.336V15m-6 0V4.575"/></svg>',
    eye: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>',
    weight: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"/></svg>',
    clock: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>',
    shield: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"/></svg>',
    check: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>',
    xmark: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>',
    question: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"/></svg>',
    moon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"/></svg>',
    calendar: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 9v9.75"/></svg>',
    user: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/></svg>',
    arrows: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3"/></svg>',
    stress: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"/></svg>',
    cake: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.871c1.355 0 2.697.056 4.024.166C17.155 8.51 18 9.473 18 10.608v2.513M15 8.25v-1.5m-6 1.5v-1.5m12 9.75-1.5.75a3.354 3.354 0 0 1-3 0 3.354 3.354 0 0 0-3 0 3.354 3.354 0 0 1-3 0 3.354 3.354 0 0 0-3 0 3.354 3.354 0 0 1-3 0L3 16.5m18-4.125a2.678 2.678 0 0 0-2.634-1.766c-.29 0-.578.023-.864.068M21 12.375v3.375m-18-3.375A2.678 2.678 0 0 1 5.634 10.609c.29 0 .578.023.864.068M3 12.375v3.375"/></svg>',
    repeat: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182M21.015 4.356v4.992"/></svg>'
  };

  /*
   * Integrity rule: SPQ_ICON_MAP keys must exactly match QUESTIONS[*].options[*].id.
   * If the key differs, the answer renders without an icon.
   */
  /** @type {Record<string, string>} */
  var SPQ_ICON_MAP = {
    /* q01: age band */
    u30: "calendar", a30_39: "calendar", a40_49: "calendar", a50p: "calendar",
    /* q03: skin state */
    state_oily: "sun", state_tight: "drop", state_reactive: "fire", state_dull: "sparkles", state_balanced: "check",
    /* q04: primary goal */
    goal_clear: "eye", goal_comfort: "heart", goal_flares: "shield", goal_firm: "hand",
    /* q06: flare pattern */
    flare_cycle: "arrows", flare_stress: "stress", flare_switching: "exclamation", flare_rare: "shield",
    /* q08: cleansing habit */
    cleanse_consistent: "heart", cleanse_skip: "moon", cleanse_aggressive: "exclamation", cleanse_inconsistent: "repeat",
    /* q09: moisturizing consistency */
    moist_twice: "check", moist_once: "moon", moist_if_dry: "drop", moist_rare: "sun",
    /* q10: nutrition/hydration */
    nutr_good: "check", nutr_busy: "stress", nutr_mixed: "scale", nutr_processed: "cake",
    /* q11: stress/sleep */
    stress_low: "check", stress_high_sleep_bad: "stress", stress_high_sleep_ok: "clock", stress_ok_sleep_bad: "moon",
    /* q14: intensity preference */
    int_simple: "bolt", int_balanced: "scale", int_full: "stack",
  };

  function getAnswerIconHtml(/** @type {*} */ optionId) {
    var key = SPQ_ICON_MAP[optionId];
    if (!key || !SPQ_ICONS[key]) return "";
    return '<span class="spq__answerIcon" aria-hidden="true">' + SPQ_ICONS[key] + '</span>';
  }

  /* 04) Pure utility helpers */
  function sanitize(/** @type {*} */ value) {
    return value === null || value === undefined ? "" : String(value);
  }

  function makeEl(/** @type {*} */ tag, /** @type {*} */ className = "", /** @type {*} */ text = null) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined && text !== null) el.textContent = text;
    return el;
  }

  function svgEl(/** @type {*} */ tag) {
    return document.createElementNS("http://www.w3.org/2000/svg", tag);
  }

  function fmt(/** @type {*} */ tpl, /** @type {*} */ values) {
    return sanitize(tpl).replace(/\{([a-zA-Z0-9_]+)\}/g, function(/** @type {*} */ _all, /** @type {*} */ key) {
      if (!values || values[key] === undefined || values[key] === null) return "";
      return String(values[key]);
    });
  }

  function validEmail(/** @type {*} */ value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || "");
  }

  function parseVariantId(/** @type {*} */ raw) {
    if (raw === null || raw === undefined) return null;
    var match = String(raw).match(/(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  function cloneItems(/** @type {*} */ items) {
    /** @type {AnyRecord[]} */
    var source = Array.isArray(items) ? items : [];
    /** @type {AnyRecord[]} */
    var out = [];
    for (var i = 0; i < source.length; i++) {
      var sourceItem = source[i];
      if (!sourceItem) continue;
      /** @type {AnyRecord} */
      var copy = {};
      var keys = Object.keys(sourceItem || {});
      for (var j = 0; j < keys.length; j++) {
        var key = keys[j];
        if (!key) continue;
        copy[key] = sourceItem[key];
      }
      out.push(copy);
    }
    return out;
  }

  function uniqueByVariant(/** @type {*} */ items) {
    /** @type {AnyRecord[]} */
    var out = [];
    /** @type {Record<number, boolean>} */
    var seen = {};
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item) continue;
      var id = parseVariantId(item && item.variant_id);
      if (!id || seen[id]) continue;
      seen[id] = true;
      out.push(item);
    }
    return out;
  }

  function ensureQuestionMaps() {
    var qids = Object.keys(QUESTIONS);
    for (var i = 0; i < qids.length; i++) {
      var qid = qids[i];
      if (!qid) continue;
      var q = QUESTIONS[qid];
      if (!q) continue;
      if (q.optionMap) continue;
      q.optionMap = {};
      for (var j = 0; j < q.options.length; j++) {
        var option = q.options[j];
        if (!option || !option.id) continue;
        q.optionMap[option.id] = option;
      }
    }
  }

  /* 04b) Pure scoring core (module scope so Node test scripts can drive it) */
  var GOAL_QUESTION_ID = "q04_primary_skin_goal";

  function getOptionFrom(/** @type {*} */ answers, /** @type {*} */ qid) {
    var q = QUESTIONS[qid];
    if (!q || !q.optionMap) return null;
    var oid = answers ? answers[qid] : null;
    return (oid && q.optionMap[oid]) || null;
  }

  function computeProfileScores(/** @type {*} */ answers) {
    /** @type {Record<string, number>} */
    var scores = { oily_combo: 0, dry_normal: 0, acne_prone: 0, anti_aging: 0 };
    /** @type {Record<string, number>} */
    var hits = { oily_combo: 0, dry_normal: 0, acne_prone: 0, anti_aging: 0 };
    var qids = Object.keys(answers || {});
    for (var i = 0; i < qids.length; i++) {
      var qid = qids[i];
      if (!qid) continue;
      var opt = getOptionFrom(answers, qid);
      if (!opt) continue;
      /* Stated goal is the customer's intent: it outweighs any single state answer. */
      var primaryWeight = qid === GOAL_QUESTION_ID ? 3 : 2;
      if (opt.primary && scores[opt.primary] !== undefined) {
        scores[opt.primary] = (scores[opt.primary] || 0) + primaryWeight;
        hits[opt.primary] = (hits[opt.primary] || 0) + 1;
      }
      if (opt.secondary && scores[opt.secondary] !== undefined) scores[opt.secondary] = (scores[opt.secondary] || 0) + 1;
    }

    /*
     * Age routing: anti-aging needs age support. 45+ boosts it, under 30 suppresses it —
     * but an explicit firmness goal (+3) can still outvote the suppression.
     */
    var ageOpt = getOptionFrom(answers, "q01_age_band");
    var age = ageOpt && typeof ageOpt.ageBand === "number" ? ageOpt.ageBand : 0;
    if (age >= 45) scores.anti_aging = (scores.anti_aging || 0) + 2;
    else if (age >= 35) scores.anti_aging = (scores.anti_aging || 0) + 1;
    else if (age > 0) scores.anti_aging = Math.max(0, (scores.anti_aging || 0) - 2);

    return { scores: scores, hits: hits };
  }

  function chooseProfileFrom(/** @type {*} */ answers) {
    var payload = computeProfileScores(answers);
    var ranked = PROFILE_KEYS.slice().sort(function(/** @type {*} */ a, /** @type {*} */ b) {
      if ((payload.scores[b] || 0) === (payload.scores[a] || 0)) return a < b ? -1 : 1;
      return (payload.scores[b] || 0) - (payload.scores[a] || 0);
    });
    if (!ranked.length) return "dry_normal";

    var topKey = ranked[0];
    if (!topKey) return "dry_normal";
    var top = payload.scores[topKey];
    /** @type {string[]} */
    var tied = [];
    for (var i = 0; i < ranked.length; i++) {
      var rankedKey = ranked[i];
      if (!rankedKey) continue;
      if (payload.scores[rankedKey] === top) tied.push(rankedKey);
    }
    if (tied.length === 1) return tied[0] || "dry_normal";

    var goalOpt = getOptionFrom(answers, GOAL_QUESTION_ID);
    var goal = goalOpt && goalOpt.primary;
    if (goal && tied.indexOf(goal) !== -1) return goal;

    tied.sort(function(/** @type {*} */ a, /** @type {*} */ b) {
      if ((payload.hits[b] || 0) === (payload.hits[a] || 0)) return a < b ? -1 : 1;
      return (payload.hits[b] || 0) - (payload.hits[a] || 0);
    });
    return tied[0] || "dry_normal";
  }

  function axisAchievableMax(/** @type {*} */ qid, /** @type {*} */ axis) {
    var q = QUESTIONS[qid];
    if (!q || !Array.isArray(q.options)) return 0;
    var best = 0;
    for (var i = 0; i < q.options.length; i++) {
      var opt = q.options[i];
      var val = Number(opt && opt.axis ? opt.axis[axis] : 0) || 0;
      if (val > best) best = val;
    }
    return best;
  }

  function computeRadarScores(/** @type {*} */ answers) {
    /** @type {Record<string, number>} */
    var out = {};
    for (var i = 0; i < AXES.length; i++) {
      var axis = AXES[i];
      if (!axis) continue;
      var qids = RADAR_SOURCES[axis] || [];
      var raw = 0;
      var max = 0;
      for (var j = 0; j < qids.length; j++) {
        var qid = qids[j];
        if (!qid) continue;
        var opt = getOptionFrom(answers, qid);
        var val = Number(opt && opt.axis ? opt.axis[axis] : 0) || 0;
        raw += val;
        max += axisAchievableMax(qid, axis);
      }
      out[axis] = max > 0 ? Math.round((raw / max) * 100) : 0;
    }
    return out;
  }

  /* 05) Section runtime */
  function initSection(/** @type {*} */ root) {
    var sectionId = root.getAttribute("data-section-id");
    if (!sectionId) return;
    /** @type {SpqBootConfig} */
    var boot = (window.__SPQ__ && window.__SPQ__[sectionId]) || {};
    if (!boot.useReportBuilderV2) return;

    /*
     * Runtime navigation map:
     * 05.1 Boot config + DOM cache
     * 05.2 State model
     * 05.3 Storage + UI helpers
     * 05.4 Scoring + narrative builders
     * 05.5 Network + model assembly
     * 05.6 Rendering (radar/cards/pricing)
     * 05.7 Event handlers + bindings
     */

    /* 05.1) Boot config + DOM cache */
    var welcomeScreen = root.querySelector("#spqWelcome-" + sectionId);
    var quizScreen = root.querySelector("#spqQuiz-" + sectionId);
    var loadingScreen = root.querySelector("#spqLoading-" + sectionId);
    var resultScreen = root.querySelector("#spqResult-" + sectionId);
    var emailScreen = root.querySelector("#spqEmail-" + sectionId);
    /** @type {HTMLElement[]} */
    var screens = /** @type {HTMLElement[]} */ ([welcomeScreen, quizScreen, loadingScreen, resultScreen, emailScreen].filter(Boolean));

    var progressWrap = root.querySelector("#spqProgressWrap-" + sectionId);
    var progressTrack = progressWrap ? progressWrap.querySelector(".spq__progressTrack") : null;
    var progressBar = root.querySelector("#spqProgress-" + sectionId);
    var counterEl = root.querySelector("#spqCounter-" + sectionId);
    var welcomeKicker = welcomeScreen ? welcomeScreen.querySelector(".spq__kicker") : null;
    var welcomeTitle = welcomeScreen ? welcomeScreen.querySelector(".spq__title") : null;
    var welcomeSubtitle = welcomeScreen ? welcomeScreen.querySelector(".spq__subtitle") : null;
    var qTextEl = root.querySelector("#spqQText-" + sectionId);
    var qHintEl = root.querySelector("#spqQHint-" + sectionId);
    var answersEl = root.querySelector("#spqAnswers-" + sectionId);
    var startBtn = root.querySelector("#spqStart-" + sectionId);
    var backBtn = root.querySelector("#spqBack-" + sectionId);
    var loadingText = root.querySelector("#spqLoadingText-" + sectionId);
    var resultKicker = resultScreen ? resultScreen.querySelector(".spq__resultKicker") : null;

    /* Optional/legacy anchors: keep null-safe for older theme markup variations. */
    var resultTitle = root.querySelector("#spqResultTitle-" + sectionId);
    var resultDesc = root.querySelector("#spqResultDesc-" + sectionId);
    var resultHero = root.querySelector("#spqResultHero-" + sectionId);
    var resultAtGlance = root.querySelector("#spqResultAtGlance-" + sectionId);
    var resultInsight = root.querySelector("#spqResultInsight-" + sectionId);
    var resultMeta = root.querySelector("#spqResultMeta-" + sectionId);
    var reportSummaryEl = root.querySelector("#spqReportSummary-" + sectionId);
    var radarEl = root.querySelector("#spqRadar-" + sectionId);
    var strengthEl = root.querySelector("#spqStrengthWeakness-" + sectionId);
    var actionsEl = root.querySelector("#spqActions-" + sectionId);

    var resultTabs = root.querySelectorAll(".spq__resultTab[data-spq-view]");
    var resultPanels = root.querySelectorAll("[data-spq-panel]");
    var coreSection = root.querySelector("#spqCoreSection-" + sectionId);
    var fullSection = root.querySelector("#spqFullSection-" + sectionId);
    var optionalSection = root.querySelector("#spqOptionalSection-" + sectionId);
    var coreCards = root.querySelector("#spqCoreCards-" + sectionId);
    var fullBlocks = root.querySelector("#spqFullBlocks-" + sectionId);
    var fullCards = root.querySelector("#spqFullCards-" + sectionId);
    var addAllBtn = root.querySelector("#spqAddAll-" + sectionId);
    var addStatus = root.querySelector("#spqAddStatus-" + sectionId);
    var addAllFullBtn = root.querySelector("#spqAddAllFull-" + sectionId);
    var addFullStatus = root.querySelector("#spqAddFullStatus-" + sectionId);
    var addAllBtnDefaultLabel = normalizeButtonLabel(addAllBtn ? addAllBtn.textContent : "");
    var addAllFullBtnDefaultLabel = normalizeButtonLabel(addAllFullBtn ? addAllFullBtn.textContent : "");
    var fallbackBtn = root.querySelector("#spqFallback-" + sectionId);
    var restartBtn = root.querySelector("#spqRestart-" + sectionId);
    var retakeHintEl = resultScreen ? resultScreen.querySelector(".spq__retakeHint") : null;

    var resultSummaryEl = root.querySelector("#spqResultSummary-" + sectionId);
    var resultIntroEl = root.querySelector("#spqResultIntro-" + sectionId);
    var corePricingEl = root.querySelector("#spqCorePricing-" + sectionId);
    var corePricingBottomEl = root.querySelector("#spqCorePricingBottom-" + sectionId);
    var fullPricingEl = root.querySelector("#spqFullPricing-" + sectionId);
    var fullPricingBottomEl = root.querySelector("#spqFullPricingBottom-" + sectionId);

    var emailReportWrap = root.querySelector("#spqEmailReport-" + sectionId);
    var emailReportForm = root.querySelector("#spqEmailReportForm-" + sectionId);
    var emailReportInput = root.querySelector("#spqEmailReportInput-" + sectionId);
    var emailReportStatus = root.querySelector("#spqEmailReportStatus-" + sectionId);

    var emailForm = root.querySelector("#spqEmailForm-" + sectionId);
    var emailInput = root.querySelector("#spqEmailInput-" + sectionId);
    var emailSkipBtn = root.querySelector("#spqEmailSkip-" + sectionId);
    var emailStatus = root.querySelector("#spqEmailStatus-" + sectionId);

    /* 05.2) State model */
    /** @type {{
     *   index: number;
     *   answers: Record<string, string>;
     *   intensity: string;
     *   goalProfile: string | null;
     *   activeView: string;
     *   screen: string;
     *   productMap: AnyRecord | null;
     *   productMapPromise: Promise<AnyRecord | null> | null;
     *   variantCache: Record<string, AnyRecord | null>;
     *   result: QuizResult | null;
     * }} */
    var state = {
      index: 0,
      answers: {},
      intensity: "balanced",
      goalProfile: null,
      activeView: "core",
      screen: "welcome",
      emailHandled: false,
      emailCaptured: false,
      email: "",
      klaviyoReportSent: false,
      productMap: null,
      productMapPromise: null,
      variantCache: {},
      result: null,
    };
    /** @type {Record<string, string>} */
    var labels = boot.labels || {};
    var STORAGE_KEY = "spq_v2_state_" + sectionId;
    var STORAGE_VERSION = 3;
    var STORAGE_TTL_MS = 1000 * 60 * 60 * 24;

    /* 05.3) Storage + label helpers */
    function canUseSessionStorage() {
      try {
        var probe = "__spq_probe__";
        sessionStorage.setItem(probe, "1");
        sessionStorage.removeItem(probe);
        return true;
      } catch (e) {
        return false;
      }
    }

    var hasSessionStorage = canUseSessionStorage();

    function screenName(/** @type {*} */ screen) {
      if (screen === quizScreen) return "quiz";
      if (screen === loadingScreen) return "loading";
      if (screen === resultScreen) return "result";
      if (screen === emailScreen) return "email";
      return "welcome";
    }

    function answeredCount() {
      var count = 0;
      for (var i = 0; i < QUESTION_ORDER.length; i++) {
        var qid = QUESTION_ORDER[i];
        if (!qid) continue;
        if (state.answers && state.answers[qid]) count += 1;
      }
      return count;
    }

    function nextQuestionIndex() {
      for (var i = 0; i < QUESTION_ORDER.length; i++) {
        var qid = QUESTION_ORDER[i];
        if (!qid) continue;
        if (!(state.answers && state.answers[qid])) return i;
      }
      return QUESTION_ORDER.length - 1;
    }

    function saveState() {
      if (!hasSessionStorage) return;
      try {
        var payload = {
          v: STORAGE_VERSION,
          ts: Date.now(),
          state: {
            index: state.index,
            answers: state.answers || {},
            intensity: state.intensity,
            goalProfile: state.goalProfile,
            activeView: state.activeView,
            screen: state.screen || "welcome",
            emailHandled: !!state.emailHandled,
            emailCaptured: !!state.emailCaptured,
            result: state.result || null,
          },
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (e) {
        /* no-op */
      }
    }

    function clearSavedState() {
      if (!hasSessionStorage) return;
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* no-op */
      }
    }

    function restoreState() {
      if (!hasSessionStorage) return false;
      try {
        var raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        var payload = JSON.parse(raw);
        if (!payload || Number(payload.v) !== STORAGE_VERSION) return false;
        if (Date.now() - Number(payload.ts || 0) > STORAGE_TTL_MS) return false;

        var saved = payload.state || {};
        state.answers = saved.answers && typeof saved.answers === "object" ? saved.answers : {};
        state.intensity = saved.intensity === "simple" || saved.intensity === "full" ? saved.intensity : "balanced";
        state.goalProfile = PROFILE_KEYS.indexOf(saved.goalProfile) !== -1 ? saved.goalProfile : null;
        state.activeView = sanitize(saved.activeView || "core");
        state.emailHandled = !!saved.emailHandled;
        state.emailCaptured = !!saved.emailCaptured;
        state.result = saved.result && typeof saved.result === "object" ? saved.result : null;

        var idx = Number(saved.index);
        if (!isFinite(idx)) idx = nextQuestionIndex();
        state.index = Math.max(0, Math.min(idx, QUESTION_ORDER.length - 1));
        var nextIdx = nextQuestionIndex();
        if (answeredCount() > 0 && state.index < nextIdx) state.index = nextIdx;

        var savedScreen = sanitize(saved.screen || "");
        state.screen = savedScreen || "welcome";

        if (savedScreen === "result" && state.result) {
          render(state.result);
          if (state.activeView) setView(state.activeView);
          return true;
        }

        if (answeredCount() >= QUESTION_ORDER.length) {
          finishQuiz();
          return true;
        }

        if (answeredCount() > 0 || savedScreen === "quiz" || savedScreen === "loading") {
          showScreen(quizScreen);
          paintQuestion();
          return true;
        }

        return false;
      } catch (e) {
        return false;
      }
    }

    function label(/** @type {*} */ key, /** @type {*} */ fallback) {
      var value = labels[key];
      if (value === undefined || value === null || value === "") return fallback;
      return sanitize(value);
    }

    var isSpanish = /^es/i.test(sanitize(boot.locale || ""));

    /* Translate a hardcoded English source string for Spanish storefront locales. */
    function t(/** @type {*} */ value) {
      var key = sanitize(value);
      if (!isSpanish || !key) return key;
      return ES_STRINGS[key] || key;
    }

    /* Event + view helpers */
    function emit(/** @type {*} */ name, /** @type {*} */ detail) {
      var payload = detail || {};
      payload.section_id = sectionId;
      root.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
    }

    function emitError(/** @type {*} */ context, /** @type {*} */ message) {
      emit("spq:error", { context: context, message: sanitize(message) });
    }

    function showScreen(/** @type {*} */ screen) {
      for (var i = 0; i < screens.length; i++) {
        var screenNode = screens[i];
        if (!screenNode) continue;
        screenNode.hidden = true;
      }
      if (screen) {
        screen.hidden = false;
        state.screen = screenName(screen);
        /* Re-trigger entrance animation */
        screen.style.animation = "none";
        void screen.offsetWidth;
        screen.style.animation = "";
        saveState();
      }
    }

    function setProgress(/** @type {*} */ current, /** @type {*} */ total) {
      if (!progressWrap || !progressBar) return;
      if (!total || total <= 0) {
        progressWrap.hidden = true;
        progressBar.style.width = "0%";
        if (progressTrack) progressTrack.setAttribute("aria-valuenow", "0");
        return;
      }
      var percent = Math.round((Math.max(0, Math.min(current, total)) / total) * 100);
      progressWrap.hidden = false;
      progressBar.style.width = percent + "%";
      if (progressTrack) progressTrack.setAttribute("aria-valuenow", String(percent));
    }

    function setCounter(/** @type {*} */ text) {
      if (counterEl) counterEl.textContent = sanitize(text);
    }

    function normalizeButtonLabel(/** @type {*} */ value) {
      return sanitize(value).replace(/\s+/g, " ").trim();
    }

    function setStatus(/** @type {*} */ node, /** @type {*} */ text, /** @type {*} */ type) {
      if (!node) return;
      node.textContent = sanitize(text);
      node.classList.remove("is-success", "is-error");
      if (type === "success") node.classList.add("is-success");
      if (type === "error") node.classList.add("is-error");
    }

    function clearStatus(/** @type {*} */ node) {
      if (!node) return;
      node.textContent = "";
      node.classList.remove("is-success", "is-error");
    }

    function setAddToCartLoadingState(/** @type {*} */ button) {
      if (!button) return;
      button.textContent = normalizeButtonLabel(label("addToCartLoading", "Adding to cart..."));
      button.classList.add("is-adding");
      button.setAttribute("aria-busy", "true");
      button.disabled = true;
    }

    function restoreAddToCartState(/** @type {*} */ button, /** @type {*} */ idleLabel, /** @type {*} */ canAdd) {
      if (!button) return;
      button.textContent = normalizeButtonLabel(idleLabel);
      button.classList.remove("is-adding");
      button.removeAttribute("aria-busy");
      button.disabled = !canAdd;
    }

    function pickCartTarget() {
      /** @type {HTMLElement[]} */
      var candidates = /** @type {HTMLElement[]} */ (
        Array.prototype.slice.call(
          document.querySelectorAll(".header-actions__cart-icon, [data-testid='cart-icon'], cart-icon, a[href='/cart']")
        )
      );
      if (!candidates.length) return null;

      /** @type {HTMLElement | null} */
      var best = null;
      var bestScore = -Infinity;

      for (var i = 0; i < candidates.length; i++) {
        var node = candidates[i];
        if (!node) continue;
        var rect = node.getBoundingClientRect();
        if (!rect.width || !rect.height) continue;
        var style = window.getComputedStyle(node);
        if (!style || style.display === "none" || style.visibility === "hidden" || style.opacity === "0") continue;

        var centerX = rect.left + rect.width / 2;
        var centerY = rect.top + rect.height / 2;
        var inViewport = rect.bottom > 0 && rect.right > 0 && rect.left < window.innerWidth && rect.top < window.innerHeight;
        var distanceToTopRight = Math.hypot(window.innerWidth - centerX, centerY);
        var score = (inViewport ? 100000 : 0) - distanceToTopRight;
        if (score > bestScore) {
          bestScore = score;
          best = node;
        }
      }

      return best;
    }

    function animateBubbleToCart(/** @type {*} */ sourceButton) {
      if (!sourceButton) return Promise.resolve();
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return Promise.resolve();

      /** @type {HTMLElement | null} */
      var cartIcon = pickCartTarget();

      var sourceRect = sourceButton.getBoundingClientRect();
      if (!sourceRect.width || !sourceRect.height) return Promise.resolve();

      var startX = sourceRect.left + sourceRect.width / 2;
      var startY = sourceRect.top + sourceRect.height / 2;
      var endX = Math.max(20, window.innerWidth - 28);
      var endY = Math.max(20, Math.min(42, window.innerHeight - 20));
      if (cartIcon) {
        var cartRect = cartIcon.getBoundingClientRect();
        if (cartRect.width && cartRect.height) {
          endX = cartRect.left + cartRect.width / 2;
          endY = cartRect.top + cartRect.height / 2;
        }
      }
      var deltaX = endX - startX;
      var deltaY = endY - startY;
      var controlY = Math.min(startY, endY) - Math.max(90, Math.abs(deltaX) * 0.16);

      /** @type {HTMLElement} */
      var bubble = makeEl("span", "spq__cartFlyBubble");
      bubble.setAttribute("aria-hidden", "true");
      bubble.style.left = startX + "px";
      bubble.style.top = startY + "px";
      document.body.appendChild(bubble);

      if (typeof bubble.animate !== "function") {
        return new Promise(function(resolve) {
          bubble.style.transition = "transform 650ms cubic-bezier(0.2, 0.75, 0.35, 1), opacity 650ms ease";
          requestAnimationFrame(function() {
            bubble.style.transform = "translate(-50%, -50%) translate(" + deltaX.toFixed(2) + "px, " + deltaY.toFixed(2) + "px) scale(0.35)";
            bubble.style.opacity = "0.88";
          });
          setTimeout(function() {
            bubble.remove();
            resolve();
          }, 680);
        });
      }

      var animation = bubble.animate(
        [
          { transform: "translate(-50%, -50%) translate(0px, 0px) scale(1)", opacity: 1 },
          {
            transform: "translate(-50%, -50%) translate(" + (deltaX * 0.45).toFixed(2) + "px, " + (controlY - startY).toFixed(2) + "px) scale(1.05)",
            opacity: 1,
            offset: 0.45,
          },
          {
            transform: "translate(-50%, -50%) translate(" + deltaX.toFixed(2) + "px, " + deltaY.toFixed(2) + "px) scale(0.35)",
            opacity: 0.88,
          },
        ],
        {
          duration: 650,
          easing: "cubic-bezier(0.2, 0.75, 0.35, 1)",
          fill: "forwards",
        }
      );

      return animation.finished
        .catch(function() {
          /* no-op */
        })
        .then(function() {
          bubble.remove();
          if (cartIcon && typeof cartIcon.animate === "function") {
            cartIcon.animate(
              [
                { transform: "scale(1)" },
                { transform: "scale(1.1)" },
                { transform: "scale(1)" },
              ],
              { duration: 260, easing: "ease-out" }
            );
          }
        });
    }

    /* 05.4) Scoring + narrative builders */
    function getQuestion(/** @type {*} */ qid) {
      return QUESTIONS[qid] || null;
    }

    function getOption(/** @type {*} */ qid, /** @type {*} */ oid) {
      var q = getQuestion(qid);
      if (!q || !q.optionMap) return null;
      return q.optionMap[oid] || null;
    }

    function profileScores() {
      return computeProfileScores(state.answers);
    }

    function chooseProfile() {
      return chooseProfileFrom(state.answers);
    }

    function segment() {
      /* Age and motherhood questions were removed in the 9-question flow, so everyone gets the general track. */
      return "general";
    }

    function radarScores() {
      return computeRadarScores(state.answers);
    }

    function classify(/** @type {*} */ score) {
      if (score >= 75) return "strength";
      if (score <= 44) return "weakness";
      return "watch";
    }

    function uniquePush(/** @type {*} */ list, /** @type {*} */ value) {
      if (!value) return;
      if (list.indexOf(value) === -1) list.push(value);
    }

    function axisNote(/** @type {*} */ axis, /** @type {*} */ type, /** @type {*} */ seg, /** @type {*} */ score) {
      var value = String(Math.max(0, Math.min(100, Number(score) || 0)));
      var row = AXIS_NOTES[axis] || AXIS_NOTES.moisturizing || {};
      var tpl = row[type] || row.watch || "";
      return fmt(t(tpl), { score: value });
    }

    function scoreStatusLabel(/** @type {*} */ kind) {
      if (kind === "strength") return t("Strong");
      if (kind === "weakness") return t("Priority fix");
      return t("Needs consistency");
    }

    function actionTextByKey(/** @type {*} */ key) {
      return t(START_ACTIONS[key] || STOP_ACTIONS[key] || KEEP_ACTIONS[key] || "");
    }

    function actionAppliesToAxis(/** @type {*} */ tag, /** @type {*} */ axis) {
      var mapped = ACTION_TAG_AXES[tag] || [];
      return mapped.indexOf(axis) !== -1;
    }

    function collectAxisActionKeys(/** @type {*} */ axis, /** @type {*} */ kind) {
      /** @type {string[]} */
      var out = [];
      var qids = Object.keys(state.answers);
      for (var i = 0; i < qids.length; i++) {
        var qid = qids[i];
        if (!qid) continue;
        var opt = getOption(qid, state.answers[qid]);
        /** @type {string[]} */
        var tags = opt && Array.isArray(opt[kind]) ? opt[kind] : [];
        for (var j = 0; j < tags.length; j++) {
          var tag = tags[j];
          if (!tag) continue;
          if (actionAppliesToAxis(tag, axis)) uniquePush(out, tag);
        }
      }
      return out;
    }

    function ensureSentence(/** @type {*} */ text) {
      var value = sanitize(text).trim();
      if (!value) return "";
      if (/[.!?]$/.test(value)) return value;
      return value + ".";
    }

    function sentenceFromOption(/** @type {*} */ opt) {
      var sub = t(sanitize(opt && opt.sub).trim());
      var text = t(sanitize(opt && opt.text).trim());
      return ensureSentence(sub || text);
    }

    function axisSignals(/** @type {*} */ axis) {
      /** @type {{ positive: string[]; negative: string[] }} */
      var out = { positive: [], negative: [] };
      var qids = RADAR_SOURCES[axis] || [];
      for (var i = 0; i < qids.length; i++) {
        var qid = qids[i];
        if (!qid) continue;
        var opt = getOption(qid, state.answers[qid]);
        var val = opt && opt.axis && typeof opt.axis[axis] === "number" ? opt.axis[axis] : 0;
        var sentence = sentenceFromOption(opt);
        if (!sentence || !val) continue;
        if (val >= 3) uniquePush(out.positive, sentence);
        else uniquePush(out.negative, sentence);
      }
      return out;
    }

    function joinPoints(/** @type {*} */ items, /** @type {*} */ fallback) {
      /** @type {string[]} */
      var clean = [];
      for (var i = 0; i < items.length; i++) {
        var line = ensureSentence(items[i]);
        if (line) clean.push(line);
      }
      if (!clean.length) return ensureSentence(fallback);
      /** @type {string[]} */
      var out = [];
      for (var j = 0; j < clean.length && j < 3; j++) {
        var cleanLine = clean[j];
        if (!cleanLine) continue;
        out.push(String(j + 1) + ". " + cleanLine);
      }
      return out.join(" ");
    }

    function axisAreaName(/** @type {*} */ axis) {
      return AXIS_LABELS[axis] ? t(AXIS_LABELS[axis]).toLowerCase() : t("this area");
    }

    function axisRightFallback(/** @type {*} */ axis, /** @type {*} */ kind) {
      var name = axisAreaName(axis);
      if (kind === "strength") return fmt(t("What you already do for {area} is keeping your skin steady."), { area: name });
      if (kind === "watch") return fmt(t("Your {area} habits help, but they are not there every week yet."), { area: name });
      return fmt(t("You have made a start on {area}, but the gaps still outweigh it."), { area: name });
    }

    function axisRiskFallback(/** @type {*} */ axis, /** @type {*} */ kind) {
      var name = axisAreaName(axis);
      if (kind === "strength") return t("The main risk is changing too much at once.");
      if (kind === "watch") return fmt(t("Skipping {area} is costing you most of what your routine could do."), { area: name });
      return fmt(t("Rarely doing {area} is feeding your breakouts and slowing recovery."), { area: name });
    }

    function axisFallbackKeys(/** @type {*} */ axis, /** @type {*} */ kind, /** @type {*} */ seg) {
      /** @type {Record<string, string[]>} */
      var row = AXIS_CHANGE_KEYS[axis] || {};
      /** @type {string[]} */
      var keys = row[kind] ? row[kind].slice(0) : [];
      if (seg === "mother_40_55" && axis === "stress") uniquePush(keys, "protected_me_time");
      return keys;
    }

    function axisChangeActions(/** @type {*} */ axis, /** @type {*} */ score, /** @type {*} */ seg) {
      /** @type {string[]} */
      var keys = [];
      /** @type {string[]} */
      var start = collectAxisActionKeys(axis, "start");
      /** @type {string[]} */
      var stop = collectAxisActionKeys(axis, "stop");
      /** @type {string[]} */
      var keep = collectAxisActionKeys(axis, "keep");
      for (var i = 0; i < start.length; i++) uniquePush(keys, start[i]);
      for (var j = 0; j < stop.length; j++) uniquePush(keys, stop[j]);
      for (var k = 0; k < keep.length; k++) uniquePush(keys, keep[k]);

      var fallbackKeys = axisFallbackKeys(axis, classify(score), seg);
      for (var n = 0; n < fallbackKeys.length; n++) uniquePush(keys, fallbackKeys[n]);

      /** @type {string[]} */
      var out = [];
      for (var z = 0; z < keys.length; z++) {
        var text = actionTextByKey(keys[z]);
        if (text) uniquePush(out, text);
      }
      return out.slice(0, 3);
    }

    function axisDetails(/** @type {*} */ axis, /** @type {*} */ score, /** @type {*} */ seg) {
      /* Convert raw score + answer-derived signals into render-ready copy for one axis card. */
      var kind = classify(score);
      var signals = axisSignals(axis);
      return {
        label: t(AXIS_LABELS[axis]),
        score: Number(score || 0),
        kind: kind,
        status: scoreStatusLabel(kind),
        meaning: axisNote(axis, kind, seg, score),
        right: joinPoints(signals.positive, axisRightFallback(axis, kind)),
        wrong: joinPoints(signals.negative, axisRiskFallback(axis, kind)),
        change: joinPoints(
          axisChangeActions(axis, score, seg),
          t("Keep it simple for 14 days, then check this score again.")
        ),
      };
    }

    function collectActionTags(/** @type {*} */ kind) {
      /** @type {string[]} */
      var out = [];
      var qids = Object.keys(state.answers);
      for (var i = 0; i < qids.length; i++) {
        var qid = qids[i];
        if (!qid) continue;
        var opt = getOption(qid, state.answers[qid]);
        /** @type {string[]} */
        var tags = opt && Array.isArray(opt[kind]) ? opt[kind] : [];
        for (var j = 0; j < tags.length; j++) uniquePush(out, tags[j]);
      }
      return out;
    }

    function toActionText(/** @type {*} */ keys, /** @type {*} */ library) {
      /** @type {string[]} */
      var out = [];
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        if (!key) continue;
        if (library[key]) uniquePush(out, library[key]);
      }
      return out;
    }

    function actionPlan(/** @type {*} */ scores, /** @type {*} */ seg) {
      /* Merge score-triggered actions with tagged answer actions, then trim to a top-3 per bucket. */
      /** @type {string[]} */
      var startKeys = [];
      /** @type {string[]} */
      var stopKeys = [];
      /** @type {string[]} */
      var keepKeys = [];

      if (scores.cleansing <= 44) uniquePush(startKeys, "consistent_evening_cleanse");
      if (scores.moisturizing <= 44) uniquePush(startKeys, "barrier_moisture_lock");
      if (scores.nutrition <= 60) uniquePush(startKeys, "hydration_nutrition_support");
      if (scores.stress <= 55) uniquePush(startKeys, "stress_sleep_reset");
      if (scores.hormonal_balance <= 55) uniquePush(startKeys, "cycle_pattern_tracking");
      if (scores.cleansing <= 60 && scores.moisturizing <= 60) uniquePush(startKeys, "simplify_to_core_steps");
      if (scores.cleansing <= 55) uniquePush(startKeys, "weekly_detox_mask");
      uniquePush(startKeys, "daily_spf_anchor");
      if (seg === "mother_40_55") uniquePush(startKeys, "protected_me_time");

      if (scores.cleansing <= 50) uniquePush(stopKeys, "harsh_scrubbing");
      if (scores.cleansing <= 60) uniquePush(stopKeys, "skipping_evening_cleanse");
      if (scores.stress <= 55) uniquePush(stopKeys, "sleeping_late_consistently");
      if (scores.nutrition <= 60) uniquePush(stopKeys, "under_hydrating");
      if (scores.hormonal_balance <= 55) uniquePush(stopKeys, "no_pattern_tracking");
      uniquePush(stopKeys, "product_hopping");
      uniquePush(stopKeys, "over_exfoliating");

      if (scores.cleansing >= 75) uniquePush(keepKeys, "gentle_consistent_cleansing");
      if (scores.moisturizing >= 75) uniquePush(keepKeys, "steady_basic_routine");
      if (scores.nutrition >= 75) uniquePush(keepKeys, "balanced_food_habit");
      if (scores.stress >= 75) uniquePush(keepKeys, "stress_management_habit");
      if (scores.hormonal_balance >= 75) uniquePush(keepKeys, "treatment_boundaries");
      uniquePush(keepKeys, "daily_spf_anchor");
      uniquePush(keepKeys, "hydration_habit");

      var extraStart = collectActionTags("start");
      var extraStop = collectActionTags("stop");
      var extraKeep = collectActionTags("keep");
      for (var i2 = 0; i2 < extraStart.length; i2++) uniquePush(startKeys, extraStart[i2]);
      for (var j2 = 0; j2 < extraStop.length; j2++) uniquePush(stopKeys, extraStop[j2]);
      for (var k2 = 0; k2 < extraKeep.length; k2++) uniquePush(keepKeys, extraKeep[k2]);

      function top3(/** @type {*} */ items, /** @type {*} */ fallback) {
        /** @type {string[]} */
        var out = [];
        for (var i3 = 0; i3 < items.length && out.length < 3; i3++) uniquePush(out, items[i3]);
        for (var j3 = 0; j3 < fallback.length && out.length < 3; j3++) uniquePush(out, fallback[j3]);
        return out.slice(0, 3);
      }

      var startText = toActionText(startKeys, START_ACTIONS);
      var stopText = toActionText(stopKeys, STOP_ACTIONS);
      var keepText = toActionText(keepKeys, KEEP_ACTIONS);
      var plan = {
        start: top3(startText, [START_ACTIONS.simplify_to_core_steps, START_ACTIONS.daily_spf_anchor, START_ACTIONS.barrier_moisture_lock]),
        stop: top3(stopText, [STOP_ACTIONS.product_hopping, STOP_ACTIONS.over_exfoliating, STOP_ACTIONS.skipping_evening_cleanse]),
        keep: top3(keepText, [KEEP_ACTIONS.steady_basic_routine, KEEP_ACTIONS.daily_spf_anchor, KEEP_ACTIONS.hydration_habit]),
      };
      emit("spq:actions_generated_v2", { start_count: plan.start.length, stop_count: plan.stop.length, keep_count: plan.keep.length });
      return plan;
    }

    function mapTier() {
      return TIER_BY_INTENSITY[state.intensity] || "balanced";
    }

    function profileCopy(/** @type {*} */ profileKey, /** @type {*} */ seg) {
      var p = PROFILE_META[profileKey] || PROFILE_META.dry_normal || { title: "", summary: "", motherSummary: "" };
      return { title: t(p.title), summary: t(seg === "mother_40_55" ? p.motherSummary : p.summary) };
    }

    function fallbackCollection(/** @type {*} */ profileKey) {
      var cols = boot.fallbackCollections || {};
      return cols[profileKey] || "/collections/all";
    }

    /* 05.5) Network + model assembly helpers */
    function productMap() {
      if (state.productMap) return Promise.resolve(state.productMap);
      if (state.productMapPromise) return state.productMapPromise;
      var url = boot.productMapUrl || "/assets/spq-product-map.json";
      state.productMapPromise = fetch(url, { credentials: "same-origin" })
        .then(function(/** @type {*} */ r) {
          if (!r.ok) throw new Error("Failed to load product map");
          return r.json();
        })
        .then(function(/** @type {*} */ json) {
          state.productMap = json || {};
          return state.productMap;
        })
        .catch(function(/** @type {*} */ e) {
          emitError("product_map", e && e.message ? e.message : "Map load failed");
          return null;
        });
      return state.productMapPromise;
    }

    function loadVariant(/** @type {*} */ id) {
      if (!id) return Promise.resolve(null);
      if (Object.prototype.hasOwnProperty.call(state.variantCache, id)) return Promise.resolve(state.variantCache[id]);
      return fetch("/variants/" + id + ".js", { credentials: "same-origin" })
        .then(function(/** @type {*} */ r) {
          if (!r.ok) throw new Error("Variant hydration failed");
          return r.json();
        })
        .then(function(/** @type {*} */ json) {
          state.variantCache[id] = json;
          return json;
        })
        .catch(function() {
          state.variantCache[id] = null;
          return null;
        });
    }

    function hydrateItems(/** @type {*} */ items) {
      var copied = cloneItems(items || []);
      /** @type {Promise<any>[]} */
      var jobs = [];
      for (var i = 0; i < copied.length; i++) {
        (function(/** @type {*} */ item) {
          var id = parseVariantId(item.variant_id);
          item.variant_numeric_id = id;
          if (!id) return;
          jobs.push(
            loadVariant(id).then(function(/** @type {*} */ variant) {
              item.variant = variant;
              item.available = !!(variant && variant.available);
              if (!item.handle && variant && variant.handle) item.handle = variant.handle;
              if (!item.url && variant && variant.product_url) item.url = variant.product_url;
              if (!item.product_url && item.url) item.product_url = item.url;
              item.price =
                variant && typeof variant.price === "number"
                  ? new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "EUR",
                    }).format(variant.price / 100)
                  : "";
              item.image = variant && variant.featured_image && variant.featured_image.src ? variant.featured_image.src : "";
            })
          );
        })(copied[i]);
      }
      return Promise.all(jobs).then(function() {
        return copied;
      });
    }

    function coreCandidates(/** @type {*} */ tierData) {
      if (!tierData) return [];
      var morning = Array.isArray(tierData.morning) ? tierData.morning : [];
      var evening = Array.isArray(tierData.evening) ? tierData.evening : [];
      return uniqueByVariant(morning.concat(evening));
    }

    function insideOut(/** @type {*} */ scores, /** @type {*} */ mapData) {
      /* Recommend the Detox Swiss Cure only when inside-out habits are actually the weak spot. */
      var rec = mapData && mapData.inside_out && mapData.inside_out.detox_swiss_cure;
      if (!rec) return [];
      var nutrition = Number((scores && scores.nutrition) || 0);
      var stress = Number((scores && scores.stress) || 0);
      if (nutrition > 55 && stress > 55) return [];
      var cfg = boot.insideOutProducts || {};
      return [
        {
          key: "detox_swiss_cure",
          title: sanitize(rec.title || "Detox Swiss Cure"),
          variant_id: sanitize(rec.variant_id || cfg.detoxSwissCureVariantId || ""),
          product_url: sanitize(rec.product_url || cfg.detoxSwissCureUrl || ""),
          how: sanitize(rec.how || ""),
          note: sanitize(rec.note || ""),
          safety_note: sanitize(rec.safety_note || boot.supplementDisclaimer || ""),
        },
      ];
    }

    function insideOutRoutineItems(/** @type {*} */ recommendations) {
      /** @type {AnyRecord[]} */
      var out = [];
      var recs = Array.isArray(recommendations) ? recommendations : [];
      for (var i = 0; i < recs.length; i++) {
        var rec = recs[i];
        if (!rec) continue;
        var variantId = parseVariantId(rec.variant_id);
        if (!variantId) continue;
        var productUrl = sanitize(rec.product_url || "");
        out.push({
          key: sanitize(rec.key || "inside_out_support"),
          title: sanitize(rec.title || "Inside-out support"),
          role: "Inside-out support (optional)",
          category: "Optional support",
          variant_id: String(variantId),
          product_url: productUrl,
          url: productUrl,
          how_to_use: sanitize(rec.how || ""),
          note: sanitize(rec.note || ""),
          safety_note: sanitize(rec.safety_note || ""),
          is_inside_out: true,
        });
      }
      return out;
    }

    function buildModel() {
      /* Single source of truth for the result model consumed by all render paths. */
      var profileKey = chooseProfile();
      var seg = segment();
      var profile = profileCopy(profileKey, seg);
      var tier = mapTier();
      /** @type {Record<string, number>} */
      var scores = radarScores();
      /** @type {Record<string, string[]>} */
      var plan = actionPlan(scores, seg);
      var mapTitle = PROFILE_TO_MAP_TITLE[profileKey] || PROFILE_TO_MAP_TITLE.dry_normal || "Dry & Normal Skin";

      emit("spq:radar_scored_v2", {
        profile: profileKey,
        cleansing: scores.cleansing,
        nutrition: scores.nutrition,
        hormonal_balance: scores.hormonal_balance,
        stress: scores.stress,
        moisturizing: scores.moisturizing,
      });

      return productMap().then(function(/** @type {*} */ mapData) {
        var tierData = mapData && mapData[mapTitle] && mapData[mapTitle][tier] ? mapData[mapTitle][tier] : null;
        var core = coreCandidates(tierData || {});
        var morning = cloneItems((tierData && tierData.morning) || []);
        var evening = cloneItems((tierData && tierData.evening) || []);
        var night = cloneItems((tierData && tierData.night) || []);
        var optional = cloneItems((tierData && tierData.optional) || []);
        var insideOutRecommendations = insideOut(scores, mapData);
        var insideOutItems = insideOutRoutineItems(insideOutRecommendations);
        var all = core.concat(morning, evening, night, optional, insideOutItems);

        return hydrateItems(all).then(function(/** @type {*} */ hydrated) {
          /** @type {Record<number, AnyRecord>} */
          var byId = {};
          for (var i = 0; i < hydrated.length; i++) {
            var hydratedItem = hydrated[i];
            if (!hydratedItem) continue;
            var id = parseVariantId(hydratedItem && hydratedItem.variant_id);
            if (id) byId[id] = hydratedItem;
          }

          function assign(/** @type {*} */ list) {
            /** @type {AnyRecord[]} */
            var out = [];
            for (var j = 0; j < list.length; j++) {
              var listItem = list[j];
              var id2 = parseVariantId(listItem && listItem.variant_id);
              out.push(id2 && byId[id2] ? byId[id2] : listItem);
            }
            return out;
          }

          var resolvedCore = assign(core);
          /** @type {AnyRecord[]} */
          var pickedCore = [];
          for (var k = 0; k < resolvedCore.length; k++) {
            var resolvedItem = resolvedCore[k];
            if (!resolvedItem) continue;
            if (resolvedItem.available === false) continue;
            pickedCore.push(resolvedItem);
            if (pickedCore.length >= 3) break;
          }
          if (pickedCore.length < 3) {
            for (var m = 0; m < resolvedCore.length; m++) {
              var oosResolvedItem = resolvedCore[m];
              if (!oosResolvedItem || oosResolvedItem.available !== false) continue;
              pickedCore.push(oosResolvedItem);
              if (pickedCore.length >= 3) break;
            }
          }

          /** @type {number[]} */
          var bundleIds = [];
          var outOfStock = 0;
          /** @type {Record<number, boolean>} */
          var seen = {};
          for (var n = 0; n < pickedCore.length; n++) {
            var pickedCoreItem = pickedCore[n];
            if (!pickedCoreItem) continue;
            var vid = parseVariantId(pickedCoreItem.variant_id);
            if (!vid || seen[vid]) continue;
            seen[vid] = true;
            if (pickedCoreItem.available === false) {
              outOfStock += 1;
              continue;
            }
            bundleIds.push(vid);
          }

          /** @type {{ strengths: AnyRecord[]; watch: AnyRecord[]; weaknesses: AnyRecord[] }} */
          var sw = { strengths: [], watch: [], weaknesses: [] };
          for (var z = 0; z < AXES.length; z++) {
            var axis = AXES[z];
            if (!axis) continue;
            var score = Number(scores[axis] || 0);
            var kind = classify(score);
            var item = { key: axis, label: t(AXIS_LABELS[axis]), score: score, note: axisNote(axis, kind, seg, score), kind: kind };
            if (kind === "strength") sw.strengths.push(item);
            else if (kind === "weakness") sw.weaknesses.push(item);
            else sw.watch.push(item);
          }

          return {
            segment: seg,
            profileKey: profileKey,
            profileTitle: profile.title,
            profileSummary: profile.summary,
            tier: tier,
            radarScores: scores,
            strengths: sw.strengths,
            watch: sw.watch,
            weaknesses: sw.weaknesses,
            actions: plan,
            insideOutRecommendations: assign(insideOutItems),
            fallbackCollection: fallbackCollection(profileKey),
            hasMap: !!tierData,
            routine: {
              core: pickedCore,
              morning: assign(morning),
              evening: assign(evening),
              night: assign(night),
              optional: assign(optional),
            },
            bundleVariantIds: bundleIds,
            bundleOutOfStockCount: outOfStock,
          };
        });
      });
    }

    /* 05.6) Rendering */
    function drawRadar(/** @type {*} */ scores) {
      /** @type {Record<string, number>} */
      var scoreMap = scores || {};
      var size = 260;
      var center = size / 2;
      var radius = 82;
      var step = (Math.PI * 2) / AXES.length;
      var start = -Math.PI / 2;

      function pt(/** @type {*} */ index, /** @type {*} */ pct) {
        var angle = start + step * index;
        var r = radius * (Math.max(0, Math.min(100, pct)) / 100);
        return { x: center + Math.cos(angle) * r, y: center + Math.sin(angle) * r };
      }
      function poly(/** @type {*} */ scale) {
        /** @type {string[]} */
        var p = [];
        for (var i = 0; i < AXES.length; i++) {
          var point = pt(i, scale);
          p.push(point.x.toFixed(2) + "," + point.y.toFixed(2));
        }
        return p.join(" ");
      }
      function data() {
        /** @type {string[]} */
        var p = [];
        for (var i = 0; i < AXES.length; i++) {
          var axis = AXES[i];
          if (!axis) continue;
          var point = pt(i, Number(scoreMap[axis] || 0));
          p.push(point.x.toFixed(2) + "," + point.y.toFixed(2));
        }
        return p.join(" ");
      }

      var svg = svgEl("svg");
      svg.setAttribute("viewBox", "0 0 " + size + " " + size);
      svg.setAttribute("class", "spq__radarSvg");
      svg.setAttribute("role", "img");
      svg.setAttribute(
        "aria-label",
        label("radarChartAriaLabel", "Radar chart for cleansing, nutrition, hormonal balance, stress resilience, and moisturizing")
      );

      var rings = [25, 50, 75, 100];
      for (var r = 0; r < rings.length; r++) {
        var ringScale = rings[r];
        if (ringScale === undefined) continue;
        var ring = svgEl("polygon");
        ring.setAttribute("points", poly(ringScale));
        ring.setAttribute("class", "spq__radarRing");
        svg.appendChild(ring);
      }

      /* Keep polygon and points below label layers for visual clarity and readable labels. */
      var shape = svgEl("polygon");
      shape.setAttribute("points", data());
      shape.setAttribute("class", "spq__radarData");
      svg.appendChild(shape);

      for (var a = 0; a < AXES.length; a++) {
        var axisKey = AXES[a];
        if (!axisKey) continue;

        var scorePoint = pt(a, Number(scoreMap[axisKey] || 0));
        var point = svgEl("circle");
        point.setAttribute("cx", scorePoint.x.toFixed(2));
        point.setAttribute("cy", scorePoint.y.toFixed(2));
        point.setAttribute("r", "4");
        point.setAttribute("class", "spq__radarPoint");
        point.setAttribute("data-spq-axis-key", axisKey);
        svg.appendChild(point);

        var labelText = t(AXIS_LABELS[axisKey] || "Axis");
        var words = labelText.split(/\s+/).filter(Boolean);
        var labelLines = words.length === 2 ? words : words.length > 2 ? [words[0], words.slice(1).join(" ")] : [labelText];
        var labelPt = pt(a, 128);
        var longestLine = 0;
        for (var lw = 0; lw < labelLines.length; lw++) {
          var labelLine = labelLines[lw];
          if (!labelLine) continue;
          if (labelLine.length > longestLine) longestLine = labelLine.length;
        }
        var labelWidth = Math.max(72, longestLine * 7 + 16);
        var labelHeight = labelLines.length > 1 ? 26 : 18;
        var labelBox = svgEl("rect");
        labelBox.setAttribute("x", (labelPt.x - labelWidth / 2).toFixed(2));
        labelBox.setAttribute("y", (labelPt.y - labelHeight / 2).toFixed(2));
        labelBox.setAttribute("width", labelWidth.toFixed(2));
        labelBox.setAttribute("height", String(labelHeight));
        labelBox.setAttribute("rx", labelLines.length > 1 ? "11" : "9");
        labelBox.setAttribute("class", "spq__radarAxisLabelBox");
        labelBox.setAttribute("data-spq-axis-key", axisKey);
        svg.appendChild(labelBox);

        var axisLabel = svgEl("text");
        axisLabel.setAttribute("x", labelPt.x.toFixed(2));
        axisLabel.setAttribute("y", labelPt.y.toFixed(2));
        axisLabel.setAttribute("text-anchor", "middle");
        axisLabel.setAttribute("dominant-baseline", "middle");
        axisLabel.setAttribute("class", "spq__radarAxisLabel");
        axisLabel.setAttribute("data-spq-axis-key", axisKey);
        if (labelLines.length > 1) {
          for (var li = 0; li < labelLines.length; li++) {
            var lineText = labelLines[li];
            if (!lineText) continue;
            var tspan = svgEl("tspan");
            tspan.setAttribute("x", labelPt.x.toFixed(2));
            tspan.setAttribute("dy", li === 0 ? "-0.35em" : "1.1em");
            tspan.textContent = lineText;
            axisLabel.appendChild(tspan);
          }
        } else {
          axisLabel.textContent = labelText;
        }
        svg.appendChild(axisLabel);

        var hit = svgEl("rect");
        hit.setAttribute("x", (labelPt.x - labelWidth / 2).toFixed(2));
        hit.setAttribute("y", (labelPt.y - labelHeight / 2).toFixed(2));
        hit.setAttribute("width", labelWidth.toFixed(2));
        hit.setAttribute("height", String(labelHeight));
        hit.setAttribute("rx", labelLines.length > 1 ? "11" : "9");
        hit.setAttribute("class", "spq__radarAxisHit");
        hit.setAttribute("data-spq-axis-key", axisKey);
        hit.setAttribute("data-spq-axis-hit", axisKey);
        hit.setAttribute("tabindex", "0");
        hit.setAttribute("role", "button");
        hit.setAttribute("aria-label", fmt(t("Show {axis} score breakdown"), { axis: labelText }));
        svg.appendChild(hit);
      }
      return svg;
    }

    function bindRadarAxisSelection(/** @type {*} */ svg, /** @type {*} */ onSelect) {
      if (!svg || typeof onSelect !== "function") return;
      var hits = svg.querySelectorAll("[data-spq-axis-hit]");
      for (var i = 0; i < hits.length; i++) {
        var currentHit = hits[i];
        if (!currentHit) continue;
        (function(/** @type {*} */ hit) {
          var axisKey = hit.getAttribute("data-spq-axis-hit");
          if (!axisKey) return;
          hit.addEventListener("click", function() {
            onSelect(axisKey, "radar");
          });
          hit.addEventListener("keydown", function(/** @type {*} */ event) {
            if (!event) return;
            if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
              event.preventDefault();
              onSelect(axisKey, "radar");
            }
          });
        })(currentHit);
      }
    }

    function setAxisHighlight(/** @type {*} */ scope, /** @type {*} */ axisKey) {
      if (!scope || !axisKey) return;
      var nodes = scope.querySelectorAll("[data-spq-axis-key]");
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        if (!node) continue;
        var nodeAxis = node.getAttribute("data-spq-axis-key");
        if (nodeAxis === axisKey) node.classList.add("is-active");
        else node.classList.remove("is-active");
      }
    }

    function extremeAxis(/** @type {*} */ scores, /** @type {*} */ mode) {
      var scoreMap = scores || {};
      /** @type {{ key: string; score: number }} */
      var picked = { key: AXES[0] || "cleansing", score: Number(scoreMap[AXES[0] || "cleansing"] || 0) };
      for (var i = 0; i < AXES.length; i++) {
        var axis = AXES[i];
        if (!axis) continue;
        var value = Number(scoreMap[axis] || 0);
        if (mode === "max" && value > picked.score) picked = { key: axis, score: value };
        if (mode === "min" && value < picked.score) picked = { key: axis, score: value };
      }
      return picked;
    }

    function renderResultHero(/** @type {*} */ model) {
      if (!resultAtGlance) return;
      resultAtGlance.innerHTML = "";

      var card = makeEl("section", "spq__reportCard spq__resultAtGlanceCard");
      card.appendChild(makeEl("h4", "spq__reportTitle", label("resultAtGlanceTitle", "At a glance: your next best skin wins")));

      var scores = model && model.radarScores ? model.radarScores : {};
      var strongest = extremeAxis(scores, "max");
      var priority = extremeAxis(scores, "min");

      var strongestKind = classify(strongest.score);
      var priorityKind = classify(priority.score);
      /** @type {{ title: string; value: string; meta: string; tone: string }[]} */
      var items = [
        {
          title: label("resultStrongestLabel", "Strongest area"),
          value: t(AXIS_LABELS[strongest.key] || "Cleansing"),
          meta: String(strongest.score) + "/100 - " + scoreStatusLabel(strongestKind),
          tone: strongestKind,
        },
        {
          title: label("resultPriorityLabel", "Top priority"),
          value: t(AXIS_LABELS[priority.key] || "Cleansing"),
          meta: String(priority.score) + "/100 - " + scoreStatusLabel(priorityKind),
          tone: priorityKind,
        },
      ];

      var grid = makeEl("div", "spq__resultAtGlanceGrid");
      for (var i2 = 0; i2 < items.length; i2++) {
        var item = items[i2];
        if (!item) continue;
        var block = makeEl("article", "spq__resultAtGlanceItem spq__resultAtGlanceItem--" + item.tone);
        block.appendChild(makeEl("p", "spq__resultAtGlanceItemLabel", item.title));
        block.appendChild(makeEl("p", "spq__resultAtGlanceItemValue", item.value));
        block.appendChild(makeEl("p", "spq__resultAtGlanceItemMeta", item.meta));
        grid.appendChild(block);
      }
      card.appendChild(grid);
      resultAtGlance.appendChild(card);
      if (resultHero) resultHero.hidden = false;
    }

    function renderScoreBreakdown(/** @type {*} */ model, /** @type {*} */ onSelectAxis) {
      if (!actionsEl) return null;
      actionsEl.innerHTML = "";

      function splitNumberedPoints(/** @type {*} */ text) {
        var value = sanitize(text).trim();
        if (!value) return [];
        /** @type {{ start: number; end: number }[]} */
        var tokens = [];
        var pattern = /\d+\.\s*/g;
        var match;
        while ((match = pattern.exec(value)) !== null) {
          tokens.push({ start: match.index, end: pattern.lastIndex });
        }
        if (!tokens.length) return [];

        /** @type {string[]} */
        var out = [];
        for (var i = 0; i < tokens.length; i++) {
          var token = tokens[i];
          if (!token) continue;
          var start = token.end;
          var nextToken = i < tokens.length - 1 ? tokens[i + 1] : null;
          var end = nextToken ? nextToken.start : value.length;
          var piece = sanitize(value.slice(start, end)).replace(/\s+/g, " ").trim();
          if (piece) out.push(piece);
        }
        return out;
      }

      function axisIndex(/** @type {*} */ axisKey) {
        for (var i = 0; i < AXES.length; i++) {
          if (AXES[i] === axisKey) return i;
        }
        return -1;
      }

      function wrapIndex(/** @type {*} */ value, /** @type {*} */ length) {
        if (!length) return 0;
        return (value % length + length) % length;
      }

      function firstSentence(/** @type {*} */ text) {
        var value = sanitize(text).replace(/\s+/g, " ").trim();
        if (!value) return "";
        var match = value.match(/^.*?[.!?](\s|$)/);
        if (match && match[0]) return match[0].trim();
        return value;
      }

      function sectionBlock(/** @type {*} */ title, /** @type {*} */ text, /** @type {*} */ kind) {
        var group = makeEl("section", "spq__axisSection spq__axisSection--" + kind);
        group.appendChild(makeEl("h6", "spq__axisSectionTitle", title));
        var points = splitNumberedPoints(text);
        if (points.length) {
          var list = makeEl("ol", "spq__axisSectionList");
          for (var i = 0; i < points.length; i++) {
            list.appendChild(makeEl("li", "spq__axisSectionListItem", points[i]));
          }
          group.appendChild(list);
        } else {
          group.appendChild(makeEl("p", "spq__axisSectionText", sanitize(text)));
        }
        return group;
      }

      /** @type {{ axis: string; detail: AnyRecord }[]} */
      var details = [];
      for (var i2 = 0; i2 < AXES.length; i2++) {
        var axis = AXES[i2];
        if (!axis) continue;
        var score = Number(model && model.radarScores && model.radarScores[axis] ? model.radarScores[axis] : 0);
        details.push({
          axis: axis,
          detail: axisDetails(axis, score, model ? model.segment : "general"),
        });
      }
      /* Order cards left-to-right by attention level: lower score first, higher score last. */
      details.sort(function(/** @type {*} */ a, /** @type {*} */ b) {
        var aScore = Number(a && a.detail ? a.detail.score : 0);
        var bScore = Number(b && b.detail ? b.detail.score : 0);
        if (aScore !== bScore) return aScore - bScore;
        var rank = { weakness: 0, watch: 1, strength: 2 };
        var aKind = a && a.detail ? rank[a.detail.kind] : 1;
        var bKind = b && b.detail ? rank[b.detail.kind] : 1;
        if (aKind !== bKind) return aKind - bKind;
        var aLabel = a && a.detail && a.detail.label ? String(a.detail.label) : "";
        var bLabel = b && b.detail && b.detail.label ? String(b.detail.label) : "";
        return aLabel.localeCompare(bLabel);
      });

      if (!details.length) {
        actionsEl.appendChild(card);
        return null;
      }

      var card = makeEl("section", "spq__reportCard spq__scoreBreakdownCard");
      card.appendChild(makeEl("h4", "spq__reportTitle", label("scoreBreakdownConciseTitle", "Score breakdown by area")));

      var axisTabs = makeEl("div", "spq__axisTabs");
      axisTabs.setAttribute("role", "tablist");
      axisTabs.setAttribute("aria-label", label("scoreBreakdownTitle", "Detailed score breakdown"));
      var panel = makeEl("article", "spq__axisDetailCard spq__axisDetailCard--concise");
      panel.id = "spqAxisPanel-" + sectionId;
      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("tabindex", "0");
      var liveRegion = makeEl("p", "spq__visuallyHidden spq__axisLiveRegion");
      liveRegion.setAttribute("aria-live", "polite");

      /** @type {HTMLElement[]} */
      var axisButtons = [];
      for (var d = 0; d < details.length; d++) {
        (function(/** @type {*} */ index) {
          var detailRow = details[index];
          if (!detailRow || !detailRow.detail) return;
          var detail = detailRow.detail;
          var axisBtn = makeEl("button", "spq__axisTab spq__axisTab--" + detail.kind);
          axisBtn.type = "button";
          axisBtn.id = "spqAxisTab-" + sectionId + "-" + detailRow.axis;
          axisBtn.setAttribute("role", "tab");
          axisBtn.setAttribute("aria-controls", panel.id);
          axisBtn.setAttribute("data-spq-axis-key", detailRow.axis);
          axisBtn.setAttribute("aria-label", detail.label + " " + String(detail.score) + "/100 " + detail.status);

          var labelEl = makeEl("span", "spq__axisTabLabel", detail.label);
          var scoreEl = makeEl("span", "spq__axisTabScore", String(detail.score));
          axisBtn.appendChild(labelEl);
          axisBtn.appendChild(scoreEl);

          axisBtn.addEventListener("click", function() {
            setActiveIndex(index, false, "breakdown");
          });
          axisBtn.addEventListener("keydown", function(/** @type {*} */ event) {
            if (!event) return;
            var key = event.key;
            if (key === "ArrowRight" || key === "ArrowDown") {
              event.preventDefault();
              var right = wrapIndex(index + 1, details.length);
              setActiveIndex(right, false, "keyboard");
              if (axisButtons[right]) axisButtons[right].focus();
            } else if (key === "ArrowLeft" || key === "ArrowUp") {
              event.preventDefault();
              var left = wrapIndex(index - 1, details.length);
              setActiveIndex(left, false, "keyboard");
              if (axisButtons[left]) axisButtons[left].focus();
            } else if (key === "Home") {
              event.preventDefault();
              setActiveIndex(0, false, "keyboard");
              if (axisButtons[0]) axisButtons[0].focus();
            } else if (key === "End") {
              event.preventDefault();
              var last = details.length - 1;
              setActiveIndex(last, false, "keyboard");
              if (axisButtons[last]) axisButtons[last].focus();
            } else if (key === "Enter" || key === " " || key === "Spacebar") {
              event.preventDefault();
              setActiveIndex(index, false, "keyboard");
            }
          });
          axisTabs.appendChild(axisBtn);
          axisButtons.push(axisBtn);
        })(d);
      }

      card.appendChild(axisTabs);
      card.appendChild(panel);
      card.appendChild(liveRegion);
      actionsEl.appendChild(card);

      function paintFocus(/** @type {*} */ item) {
        if (!panel || !item || !item.detail) return;
        var detail = item.detail;
        panel.className = "spq__axisDetailCard spq__axisDetailCard--concise spq__axisBucket--" + detail.kind;
        panel.setAttribute("data-spq-axis-key", item.axis);
        panel.innerHTML = "";

        var head = makeEl("header", "spq__axisDetailHead");
        head.appendChild(makeEl("h5", "spq__axisBucketTitle", detail.label));
        panel.appendChild(head);

        var takeaway = firstSentence(detail.meaning).replace(/\(\d+\s*\/\s*100\)/g, "").replace(/\s+/g, " ").trim();
        panel.appendChild(makeEl("p", "spq__axisDetailSummary", ensureSentence(takeaway)));

        panel.appendChild(sectionBlock(label("detailRightLabel", "What you are already doing right"), detail.right, "right"));
        panel.appendChild(sectionBlock(label("detailWrongLabel", "What is likely hurting this score"), detail.wrong, "wrong"));
        panel.appendChild(sectionBlock(label("detailChangeLabel", "What to change next"), detail.change, "change"));
      }

      var activeIndex = 0;
      function setActiveIndex(/** @type {*} */ nextIndex, /** @type {*} */ silent, /** @type {*} */ source) {
        var next = wrapIndex(nextIndex, details.length);
        if (next === activeIndex && silent && source !== "init") return;
        activeIndex = next;
        var active = details[activeIndex];
        if (!active) return;
        paintFocus(active);

        for (var b = 0; b < axisButtons.length; b++) {
          var axisButton = axisButtons[b];
          if (!axisButton) continue;
          var isActive = b === activeIndex;
          axisButton.classList.toggle("is-active", isActive);
          axisButton.setAttribute("aria-selected", isActive ? "true" : "false");
          axisButton.setAttribute("tabindex", isActive ? "0" : "-1");
        }
        if (panel) panel.setAttribute("aria-labelledby", "spqAxisTab-" + sectionId + "-" + active.axis);
        if (liveRegion && source !== "init") {
          liveRegion.textContent = fmt(label("breakdownLivePrefix", "Showing {axis} score breakdown."), {
            axis: active.detail && active.detail.label ? active.detail.label : "axis",
          });
        }
        if (source && source !== "init") emit("spq:result_axis_selected", { axis: active.axis, source: source });

        if (!silent && typeof onSelectAxis === "function") onSelectAxis(active.axis, source || "cards");
      }

      setActiveIndex(0, true, "init");

      return {
        getActiveAxis: function() {
          var activeDetail = details[activeIndex];
          return activeDetail ? activeDetail.axis : "";
        },
        setActiveAxis: function(/** @type {*} */ axisKey, /** @type {*} */ silent, /** @type {*} */ source) {
          var idx = axisIndex(axisKey);
          if (idx === -1) return;
          if (idx === activeIndex && silent) return;
          setActiveIndex(idx, !!silent, source || "sync");
        },
      };
    }

    function setView(/** @type {*} */ view) {
      state.activeView = view || "core";
      for (var i = 0; i < resultPanels.length; i++) {
        var panel = resultPanels[i];
        if (!panel) continue;
        var hidden = panel.getAttribute("data-spq-panel") !== state.activeView;
        panel.hidden = hidden;
        panel.setAttribute("aria-hidden", hidden ? "true" : "false");
      }
      for (var j = 0; j < resultTabs.length; j++) {
        var tab = resultTabs[j];
        if (!tab) continue;
        var active = tab.getAttribute("data-spq-view") === state.activeView;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", active ? "true" : "false");
        tab.setAttribute("tabindex", active ? "0" : "-1");
      }
      saveState();
    }

    function firstView(/** @type {*} */ opts) {
      if (opts.core) return "core";
      if (opts.full) return "full";
      if (opts.optional) return "optional";
      return "core";
    }

    function collectCartSections() {
      var nodes = document.querySelectorAll("cart-items-component[data-section-id]");
      /** @type {string[]} */
      var out = [];
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        if (!node) continue;
        var id = node.getAttribute("data-section-id");
        if (id) out.push(id);
      }
      return out;
    }

    function cartUpdate(/** @type {*} */ resource, /** @type {*} */ count) {
      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: resource || {},
            sourceId: "spq-v2-" + sectionId,
            data: { source: "spq-v2", itemCount: count, sections: (resource && resource.sections) || {} },
          },
        })
      );
    }

    function openCart() {
      /** @type {{ open?: () => void } | null} */
      var drawer = /** @type {{ open?: () => void } | null} */ (document.querySelector("cart-drawer-component"));
      if (drawer && typeof drawer.open === "function") drawer.open();
      else window.location.href = "/cart";
    }

    function addBundle(/** @type {*} */ ids) {
      if (!ids || !ids.length) return Promise.reject(new Error("No variants available to add."));
      /** @type {{ items: Array<{ id: string | number; quantity: number }>; sections?: string; sections_url?: string }} */
      var payload = { items: [] };
      for (var i = 0; i < ids.length; i++) payload.items.push({ id: ids[i], quantity: 1 });
      var sections = collectCartSections();
      if (sections.length) {
        payload.sections = sections.join(",");
        payload.sections_url = window.location.pathname;
      }
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin",
      }).then(function(/** @type {*} */ r) {
        return r.json().then(function(/** @type {*} */ json) {
          if (!r.ok) throw new Error((json && (json.description || json.message)) || "Could not add items to cart.");
          if (json && json.items && json.items.length < ids.length) throw new Error("Only part of your routine could be added. Please try again.");
          return json;
        });
      });
    }

    /* 05.6.1) Product cards + price views */
    function moneyFmt(/** @type {*} */ cents) {
      if (typeof cents !== "number") return "";
      try {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "EUR",
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    function resolveProductUrl(/** @type {*} */ item) {
      var infoUrl = sanitize(item && (item.url || item.product_url || item.productUrl) || "").trim();
      if (!infoUrl && item && item.handle) infoUrl = "/products/" + sanitize(item.handle);
      return infoUrl;
    }

    function getItemVariantId(/** @type {*} */ item) {
      if (!item) return null;
      return parseVariantId(item.variant_id) || parseVariantId(item.variant && item.variant.id);
    }

    function itemRoleKey(/** @type {*} */ item) {
      return sanitize(item && (item.role || item.category) || "").toLowerCase().trim();
    }

    function buildAlternativeMap(/** @type {*} */ displayItems, /** @type {*} */ poolItems) {
      /** @type {Record<number, { title: string; url: string }>} */
      var map = {};
      /** @type {Record<number, boolean>} */
      var shownIds = {};
      /** @type {Record<number, boolean>} */
      var usedAltIds = {};
      var isDryNormalProfile = !!(state.result && state.result.profileKey === "dry_normal");
      var i;

      var shown = Array.isArray(displayItems) ? displayItems : [];
      var pool = Array.isArray(poolItems) ? poolItems : [];

      for (i = 0; i < shown.length; i++) {
        var shownId = getItemVariantId(shown[i]);
        if (shownId) shownIds[shownId] = true;
      }

      /** @type {AnyRecord[]} */
      var candidates = [];
      /** @type {AnyRecord[]} */
      var fallbackCandidates = [];
      for (i = 0; i < pool.length; i++) {
        var poolItem = pool[i];
        var poolId = getItemVariantId(poolItem);
        var poolUrl = resolveProductUrl(poolItem);
        if (!poolId || !poolUrl || poolItem.available === false) continue;
        if (!shownIds[poolId]) candidates.push(poolItem);
        fallbackCandidates.push(poolItem);
      }

      function pickAlternative(/** @type {*} */ target, /** @type {*} */ options) {
        var targetRole = itemRoleKey(target);
        var best = null;
        var bestScore = -1;
        for (var c = 0; c < options.length; c++) {
          var candidate = options[c];
          var candidateId = getItemVariantId(candidate);
          if (!candidateId || usedAltIds[candidateId]) continue;
          var score = 0;
          var candidateTitle = sanitize(candidate && candidate.title || "");
          if (isDryNormalProfile && /brightening\s*cream/i.test(candidateTitle)) score += 250;
          if (targetRole && itemRoleKey(candidate) === targetRole) score += 100;
          if (score > bestScore) {
            best = candidate;
            bestScore = score;
          }
        }
        if (!best) return null;
        var bestId = getItemVariantId(best);
        if (bestId) usedAltIds[bestId] = true;
        return {
          title: sanitize(best.title || "") || t("Recommended alternative"),
          url: resolveProductUrl(best),
        };
      }

      for (i = 0; i < shown.length; i++) {
        var current = shown[i];
        var currentId = getItemVariantId(current);
        if (!currentId || current.available !== false) continue;
        var alt = pickAlternative(current, candidates) || pickAlternative(current, fallbackCandidates);
        if (alt && alt.url) map[currentId] = alt;
      }

      return map;
    }

    function renderProductCard(/** @type {*} */ item, /** @type {*} */ stepNumber, /** @type {*} */ alternativeMap) {
      var card = makeEl("article", "spq__productCard");
      if (item.available === false) card.classList.add("spq__productCard--oos");

      if (item.image) {
        var img = document.createElement("img");
        img.className = "spq__productImage";
        img.src = item.image;
        img.alt = sanitize(item.title || "Product image");
        img.loading = "lazy";
        card.appendChild(img);
      }

      var body = makeEl("div", "spq__productBody");
      body.appendChild(makeEl("span", "spq__productStep", fmt(label("stepLabel", "Step {number}"), { number: stepNumber })));

      var role = item.role || item.category || "";
      if (role) body.appendChild(makeEl("p", "spq__productRole", t(sanitize(role))));

      body.appendChild(makeEl("p", "spq__productTitle", sanitize(item.title)));
      if (item.price) body.appendChild(makeEl("p", "spq__productPrice", sanitize(item.price)));

      var itemId = getItemVariantId(item);
      if (item.available === false) {
        body.appendChild(makeEl("p", "spq__productStock spq__productStock--oos", label("outOfStockLabel", "Out of stock")));
        var alternative = itemId && !item.is_inside_out && alternativeMap ? alternativeMap[itemId] : null;
        if (alternative && alternative.url) {
          var altText = makeEl("p", "spq__productAlt", label("tryInsteadLabel", "Try instead:") + " ");
          var altLink = document.createElement("a");
          altLink.className = "spq__productAltLink";
          altLink.href = alternative.url;
          altLink.textContent = alternative.title;
          altText.appendChild(altLink);
          body.appendChild(altText);
        }
      }

      var infoUrl = resolveProductUrl(item);

      if (infoUrl) {
        /* Real link (new tab) so shoppers can check details without leaving their result. */
        var infoLink = document.createElement("a");
        infoLink.className = "spq__productInfo";
        infoLink.href = infoUrl;
        infoLink.target = "_blank";
        infoLink.rel = "noopener";
        infoLink.textContent = label("moreInfoLabel", "More info") + " ";
        var infoIcon = document.createElement("span");
        infoIcon.className = "spq__productInfoIcon";
        infoIcon.setAttribute("aria-hidden", "true");
        infoIcon.textContent = "i";
        infoLink.appendChild(infoIcon);
        body.appendChild(infoLink);
      }

      card.appendChild(body);
      return card;
    }

    function discountConfig(/** @type {*} */ tier) {
      /* A saving is only ever shown when a real Shopify discount code is configured for the tier. */
      var d = boot.discounts || {};
      var code = sanitize(tier === "full" ? d.fullCode : d.coreCode).trim();
      var percent = Number(tier === "full" ? d.fullPercent : d.corePercent) || 0;
      var savingsMode = sanitize(boot.savingsMode || "none").toLowerCase();
      return {
        code: code,
        percent: percent,
        enabled: savingsMode === "display" && !!code && percent > 0,
      };
    }

    function applyDiscountCode(/** @type {*} */ tier) {
      var cfg = discountConfig(tier);
      if (!cfg.enabled) return Promise.resolve();
      /* Registers the code on the cart session so checkout applies it automatically. */
      return fetch("/discount/" + encodeURIComponent(cfg.code), { credentials: "same-origin" }).catch(function() {
        /* no-op: a failed discount fetch must not block adding to cart */
      });
    }

    function renderPricing(/** @type {*} */ container, /** @type {*} */ totalCents, /** @type {*} */ tier) {
      if (!container) return;
      container.innerHTML = "";
      if (!totalCents || totalCents <= 0) return;

      var cfg = discountConfig(tier);
      if (!cfg.enabled) {
        container.appendChild(makeEl("span", "spq__priceDiscounted", moneyFmt(totalCents)));
        return;
      }

      var discountedCents = Math.round(totalCents * (1 - cfg.percent / 100));
      container.appendChild(makeEl("span", "spq__priceOriginal", moneyFmt(totalCents)));
      container.appendChild(makeEl("span", "spq__priceDiscounted", moneyFmt(discountedCents)));
      container.appendChild(
        makeEl(
          "span",
          "spq__priceNote",
          fmt(label("discountAppliedNote", "{percent}% off is applied automatically at checkout."), { percent: cfg.percent })
        )
      );
    }

    function sumPriceCents(/** @type {*} */ items) {
      var total = 0;
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (!item) continue;
        if (item.variant && typeof item.variant.price === "number") {
          total += item.variant.price;
        }
      }
      return total;
    }

    function buildFullItems(/** @type {*} */ model) {
      /** @type {AnyRecord[]} */
      var all = [];
      /** @type {string[]} */
      var phases = ["morning", "evening", "night"];
      for (var p = 0; p < phases.length; p++) {
        var phase = phases[p];
        if (!phase) continue;
        var items = model.routine[phase] || [];
        for (var i = 0; i < items.length; i++) all.push(items[i]);
      }
      return uniqueByVariant(all);
    }

    function render(/** @type {QuizResult} */ model) {
      state.result = model;
      if (resultTitle) resultTitle.textContent = model.profileTitle;
      if (resultDesc) resultDesc.textContent = model.profileSummary;
      if (fallbackBtn) fallbackBtn.href = model.fallbackCollection || "/collections/all";
      if (resultKicker) resultKicker.textContent = label("resultKicker", "Your skin profile");
      if (resultSummaryEl) {
        resultSummaryEl.innerHTML = "";
        resultSummaryEl.hidden = true;
      }
      if (resultIntroEl) {
        resultIntroEl.textContent = "";
        resultIntroEl.hidden = true;
      }
      if (resultInsight) {
        resultInsight.innerHTML = "";
        resultInsight.hidden = true;
      }
      if (resultMeta) {
        resultMeta.innerHTML = "";
        resultMeta.hidden = true;
      }
      if (reportSummaryEl) {
        reportSummaryEl.innerHTML = "";
        reportSummaryEl.hidden = true;
      }
      if (strengthEl) {
        strengthEl.innerHTML = "";
        strengthEl.hidden = true;
      }
      if (resultHero) resultHero.hidden = false;
      renderResultHero(model);

      /** @type {HTMLElement | null} */
      var radarCard = null;
      /** @type {{ getActiveAxis: () => string; setActiveAxis: (axisKey: any, silent: any, source: any) => void } | null} */
      var scoreBreakdown = null;
      function activateAxis(/** @type {*} */ axisKey, /** @type {*} */ source) {
        if (!axisKey) return;
        if (scoreBreakdown && source !== "breakdown") scoreBreakdown.setActiveAxis(axisKey, true, source || "sync");
        if (radarCard) setAxisHighlight(radarCard, axisKey);
        if (actionsEl) setAxisHighlight(actionsEl, axisKey);
      }

      if (radarEl) {
        radarEl.innerHTML = "";
        var radarCardEl = makeEl("section", "spq__reportCard spq__radarCard");
        radarCard = radarCardEl;
        radarCardEl.appendChild(makeEl("h4", "spq__reportTitle", label("radarTitle", "Health-quality radar affecting your skin")));
        radarCardEl.appendChild(makeEl("p", "spq__radarHelper", label("radarHelperText", "Tap an axis label to inspect the score breakdown.")));
        var chartWrap = makeEl("div", "spq__radarChartWrap");
        var radarSvg = drawRadar(model.radarScores);
        bindRadarAxisSelection(radarSvg, function(/** @type {*} */ axisKey) {
          activateAxis(axisKey, "radar");
        });
        chartWrap.appendChild(radarSvg);
        radarCardEl.appendChild(chartWrap);
        radarEl.appendChild(radarCardEl);
        radarEl.hidden = false;
      }

      scoreBreakdown = renderScoreBreakdown(model, function(/** @type {*} */ axisKey) {
        activateAxis(axisKey, "breakdown");
      });
      if (scoreBreakdown && typeof scoreBreakdown.getActiveAxis === "function") {
        activateAxis(scoreBreakdown.getActiveAxis() || AXES[0], "init");
      } else {
        activateAxis(AXES[0], "init");
      }

      if (coreCards) coreCards.innerHTML = "";
      if (fullCards) fullCards.innerHTML = "";
      if (fullBlocks) fullBlocks.innerHTML = "";
      clearStatus(addStatus);
      clearStatus(addFullStatus);

      var core = model.routine.core || [];
      var hasCore = core.length > 0;
      var hasPrimaryOffer = false;
      var canAddCoreBundle = !!(model.bundleVariantIds && model.bundleVariantIds.length > 0);

      if (coreSection) coreSection.hidden = !hasCore;
      restoreAddToCartState(addAllBtn, addAllBtnDefaultLabel, canAddCoreBundle);
      hasPrimaryOffer = hasPrimaryOffer || hasCore;

      /* Core routine cards */
      if (hasCore && coreCards) {
        var coreTotalCents = sumPriceCents(core);
        renderPricing(corePricingEl, coreTotalCents, "core");
        if (corePricingBottomEl) corePricingBottomEl.innerHTML = "";

        var fullItemsForAlternatives = buildFullItems(model);
        var optionalItemsForAlternatives = uniqueByVariant(model.routine.optional || []);
        var coreAlternativeMap = buildAlternativeMap(core, fullItemsForAlternatives.concat(optionalItemsForAlternatives));

        for (var i2 = 0; i2 < core.length; i2++) {
          var coreItem = core[i2];
          if (!coreItem) continue;
          coreCards.appendChild(renderProductCard(coreItem, i2 + 1, coreAlternativeMap));
        }
      }

      /* Full routine cards */
      var insideOutItems = uniqueByVariant(model.insideOutRecommendations || []);
      var fullItems = uniqueByVariant(buildFullItems(model).concat(insideOutItems));
      var hasFull = fullItems.length > 0;
      if (fullSection) fullSection.hidden = !hasFull;
      hasPrimaryOffer = hasPrimaryOffer || hasFull;

      model.fullBundleVariantIds = [];
      for (var f = 0; f < fullItems.length; f++) {
        var fullItem = fullItems[f];
        if (!fullItem) continue;
        if (fullItem.available !== false && fullItem.variant && fullItem.variant.id) {
          model.fullBundleVariantIds.push(fullItem.variant.id);
        }
      }
      restoreAddToCartState(addAllFullBtn, addAllFullBtnDefaultLabel, model.fullBundleVariantIds.length > 0);

      var targetContainer = fullCards || fullBlocks;
      if (hasFull && targetContainer) {
        var fullTotalCents = sumPriceCents(fullItems);
        renderPricing(fullPricingEl, fullTotalCents, "full");
        if (fullPricingBottomEl) fullPricingBottomEl.innerHTML = "";

        var optionalPool = uniqueByVariant(model.routine.optional || []);
        var fullAlternativeMap = buildAlternativeMap(fullItems, optionalPool.concat(core));

        for (var j2 = 0; j2 < fullItems.length; j2++) {
          var fullCardItem = fullItems[j2];
          if (!fullCardItem) continue;
          targetContainer.appendChild(renderProductCard(fullCardItem, j2 + 1, fullAlternativeMap));
        }
      }

      if (insideOutItems.length) {
        emit("spq:inside_out_recommendation_shown", {
          count: insideOutItems.length,
          keys: insideOutItems.map(function(/** @type {*} */ item) {
            return item && item.key ? item.key : "";
          }),
          placement: "routine_products",
        });
      }

      /* Tab states */
      for (var t = 0; t < resultTabs.length; t++) {
        var resultTab = resultTabs[t];
        if (!resultTab) continue;
        var view = resultTab.getAttribute("data-spq-view");
        var enabled = (view === "core" && hasCore) || (view === "full" && hasFull);
        resultTab.disabled = !enabled;
        resultTab.setAttribute("aria-disabled", enabled ? "false" : "true");
      }
      setView(firstView({ core: hasCore, full: hasFull }));

      if (fallbackBtn) {
        fallbackBtn.hidden = hasPrimaryOffer;
        fallbackBtn.setAttribute("aria-hidden", hasPrimaryOffer ? "true" : "false");
      }

      if (emailReportWrap) {
        /*
         * The report is strictly opt-in: everyone sees the request form, and nothing is sent
         * until they ask for it. Pre-fill the address captured at the gate so it is one tap.
         */
        emailReportWrap.hidden = boot.emailEnabled === false;
      }
      if (emailReportInput && !emailReportInput.value && state.email) {
        emailReportInput.value = state.email;
      }
      showScreen(resultScreen);
      setProgress(0, 0);
      setCounter("");
      emit("spq:report_view_v2", { profile: model.profileKey, segment: model.segment, tier: model.tier, has_map: model.hasMap });
    }

    function showLoadingThenResult() {
      showScreen(loadingScreen);
      setProgress(QUESTION_ORDER.length, QUESTION_ORDER.length);
      var buildingText = label("reportBuildingText", "Building your report...");
      setCounter(buildingText);
      if (loadingText) loadingText.textContent = buildingText;
      var fallbackProfile = PROFILE_META.dry_normal || { title: "Dry & Normal Skin", summary: "" };
      buildModel()
        .then(render)
        .catch(function(/** @type {*} */ e) {
          emitError("result_build_v2", e && e.message ? e.message : "Could not build report");
          render({
            segment: "general",
            profileKey: "dry_normal",
            profileTitle: fallbackProfile.title,
            profileSummary: fallbackProfile.summary,
            tier: "balanced",
            radarScores: { cleansing: 50, nutrition: 50, hormonal_balance: 50, stress: 50, moisturizing: 50 },
            strengths: [],
            watch: [],
            weaknesses: [],
            actions: {
              start: [START_ACTIONS.simplify_to_core_steps || "", START_ACTIONS.daily_spf_anchor || "", START_ACTIONS.barrier_moisture_lock || ""],
              stop: [STOP_ACTIONS.product_hopping || "", STOP_ACTIONS.over_exfoliating || "", STOP_ACTIONS.skipping_evening_cleanse || ""],
              keep: [KEEP_ACTIONS.steady_basic_routine || "", KEEP_ACTIONS.daily_spf_anchor || "", KEEP_ACTIONS.hydration_habit || ""],
            },
            insideOutRecommendations: [],
            fallbackCollection: fallbackCollection("dry_normal"),
            hasMap: false,
            routine: { core: [], morning: [], evening: [], night: [], optional: [] },
            bundleVariantIds: [],
            bundleOutOfStockCount: 0,
          });
        });
    }

    /* 05.7) Event handlers + bindings */
    function onAnswer(/** @type {*} */ qid, /** @type {*} */ oid, /** @type {*} */ btn) {
      var nodes = answersEl ? answersEl.querySelectorAll(".spq__answer") : [];
      for (var i = 0; i < nodes.length; i++) {
        var answerNode = nodes[i];
        if (!answerNode) continue;
        answerNode.disabled = true;
      }
      if (btn) btn.classList.add("is-selected");
      state.answers[qid] = oid;
      var opt = getOption(qid, oid);
      if (qid === "q14_routine_intensity_preference" && opt && opt.intensity) state.intensity = opt.intensity;
      if (qid === "q04_primary_skin_goal" && opt && opt.primary) state.goalProfile = opt.primary;

      emit("spq:answer", {
        question_id: qid,
        answer_id: oid,
        primary_profile: opt && opt.primary ? opt.primary : null,
        secondary_profile: opt && opt.secondary ? opt.secondary : null,
        intensity: opt && opt.intensity ? opt.intensity : null,
      });

      setTimeout(function() {
        if (state.index < QUESTION_ORDER.length - 1) {
          state.index += 1;
          paintQuestion();
        } else {
          finishQuiz();
        }
      }, 90);
    }

    function finishQuiz() {
      if (boot.emailEnabled !== false && emailScreen && emailForm && !state.emailHandled) {
        clearStatus(emailStatus);
        showScreen(emailScreen);
        emit("spq:email_gate_view", { total_questions: QUESTION_ORDER.length });
        return;
      }
      showLoadingThenResult();
    }

    function onEmailGateSubmit(/** @type {*} */ event) {
      event.preventDefault();
      var email = emailInput ? sanitize(emailInput.value).trim() : "";
      if (!validEmail(email)) {
        setStatus(emailStatus, label("emailError", "Please enter a valid email address."), "error");
        emit("spq:email_gate_submit", { status: "invalid" });
        return;
      }
      state.emailHandled = true;
      state.emailCaptured = true;
      state.email = email;
      saveState();
      emit("spq:email_gate_submit", { status: "submitted" });
      /* Capture in the background so the result is never blocked on the network. */
      var action = (emailForm && emailForm.getAttribute("action")) || "/contact";
      var data = new FormData(emailForm);
      fetch(action, { method: "POST", body: data, credentials: "same-origin" }).catch(function() {
        /* no-op: lead capture must never break the result flow */
      });
      /* Add the profile to Klaviyo immediately; the report metric fires later, once the result is built. */
      klaviyoCaptureLead(email);
      showLoadingThenResult();
    }

    function onEmailGateSkip() {
      state.emailHandled = true;
      saveState();
      emit("spq:email_gate_skip", {});
      showLoadingThenResult();
    }

    function paintQuestion() {
      var qid = QUESTION_ORDER[state.index];
      if (!qid) return;
      var q = getQuestion(qid);
      if (!q) return;
      if (qTextEl) {
        qTextEl.textContent = t(q.text);
        /* Trigger question text slide-in animation */
        qTextEl.classList.remove("is-entering");
        void qTextEl.offsetWidth;
        qTextEl.classList.add("is-entering");
      }
      if (qHintEl) {
        var hint = q && q.hint ? q.hint : "";
        qHintEl.textContent = t(sanitize(hint));
        qHintEl.hidden = !hint;
      }
      if (answersEl) {
        answersEl.innerHTML = "";
        for (var i = 0; i < q.options.length; i++) {
          (function(/** @type {*} */ opt, /** @type {*} */ index) {
            var b = makeEl("button", "spq__answer");
            b.type = "button";
            /* Inject icon before answer text */
            var iconHtml = getAnswerIconHtml(opt.id);
            if (iconHtml) {
              var iconWrap = document.createElement("span");
              iconWrap.innerHTML = iconHtml;
              if (iconWrap.firstChild) b.appendChild(iconWrap.firstChild);
            }
            var copy = makeEl("span", "spq__answerCopy");
            copy.appendChild(makeEl("span", "spq__answerTitle", t(sanitize(opt.text))));
            if (opt && opt.sub) {
              copy.appendChild(makeEl("span", "spq__answerSub", t(sanitize(opt.sub))));
              b.classList.add("spq__answer--stacked");
            }
            b.appendChild(copy);
            if (state.answers[qid] === opt.id) b.classList.add("is-selected");
            b.addEventListener("click", function() {
              onAnswer(qid, opt.id, b);
            });
            answersEl.appendChild(b);
            /* Staggered entrance: each answer fades in with a small delay */
            setTimeout(function() {
              b.classList.add("is-visible");
            }, 60 * index + 30);
          })(q.options[i], i);
        }
      }
      setProgress(state.index + 1, QUESTION_ORDER.length);
      setCounter(fmt(label("questionCounterLabel", "Question {current} of {total}"), { current: state.index + 1, total: QUESTION_ORDER.length }));
      emit("spq:question_view_v2", {
        question_id: qid,
        index: state.index + 1,
        total: QUESTION_ORDER.length,
      });
      if (backBtn) backBtn.style.display = "inline-flex";
      saveState();
    }

    function normalizeTag(/** @type {*} */ value) {
      return sanitize(value)
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 40);
    }

    function appendCsvTag(/** @type {*} */ csv, /** @type {*} */ tag) {
      /** @type {string[]} */
      var out = [];
      /** @type {Record<string, boolean>} */
      var seen = {};
      var raw = sanitize(csv);
      var parts = raw ? raw.split(",") : [];
      for (var i = 0; i < parts.length; i++) {
        var cleaned = sanitize(parts[i]).trim();
        if (!cleaned || seen[cleaned]) continue;
        seen[cleaned] = true;
        out.push(cleaned);
      }
      var next = sanitize(tag).trim();
      if (next && !seen[next]) out.push(next);
      return out.join(",");
    }

    function enrichReportEmailPayload(/** @type {*} */ data) {
      var tags = sanitize(data.get("contact[tags]") || boot.emailTag || "newsletter,skin-quiz");
      tags = appendCsvTag(tags, "skin-quiz-report-v2");
      if (state.result) {
        tags = appendCsvTag(tags, "spq_profile_" + normalizeTag(state.result.profileKey || ""));
        tags = appendCsvTag(tags, "spq_tier_" + normalizeTag(state.result.tier || ""));
        tags = appendCsvTag(tags, "spq_segment_" + normalizeTag(state.result.segment || ""));
      }
      data.set("contact[tags]", tags);

      if (!state.result) return;
      /** @type {Record<string, number>} */
      var scores = state.result.radarScores || {};
      /** @type {Record<string, string[]>} */
      var actions = state.result.actions || {};
      var note = [
        "Skin quiz report v2",
        "Profile: " + sanitize(state.result.profileTitle || state.result.profileKey || ""),
        "Tier: " + sanitize(state.result.tier || ""),
        "Segment: " + sanitize(state.result.segment || ""),
        "Radar: cleansing " + String(Number(scores.cleansing || 0)),
        "nutrition " + String(Number(scores.nutrition || 0)),
        "hormonal_balance " + String(Number(scores.hormonal_balance || 0)),
        "stress " + String(Number(scores.stress || 0)),
        "moisturizing " + String(Number(scores.moisturizing || 0)),
        "Start: " + sanitize(Array.isArray(actions.start) ? actions.start.join(" / ") : ""),
        "Stop: " + sanitize(Array.isArray(actions.stop) ? actions.stop.join(" / ") : ""),
        "Keep: " + sanitize(Array.isArray(actions.keep) ? actions.keep.join(" / ") : ""),
        "Fallback: " + sanitize(state.result.fallbackCollection || ""),
      ].join(" | ");
      data.set("contact[note]", note.slice(0, 2000));
    }

    function submitReportEmail(/** @type {*} */ form) {
      if (!form) return Promise.resolve(false);
      var action = form.getAttribute("action") || "/contact";
      var data = new FormData(form);
      enrichReportEmailPayload(data);
      return fetch(action, { method: "POST", body: data, credentials: "same-origin" })
        .then(function(/** @type {*} */ r) {
          return r.ok;
        })
        .catch(function() {
          return false;
        });
    }

    /*
     * 05.7.1) Klaviyo integration.
     * Adds the profile to Klaviyo and fires a "Completed Skin Quiz" metric carrying the full
     * report, so a Klaviyo flow can deliver the personalized report email.
     * - Metric/identify use the onsite window.klaviyo (installed by the Klaviyo app) with a
     *   legacy _learnq fallback; no key needed in the theme.
     * - Consented list subscription uses the client REST API, which needs the public key +
     *   list id from section settings. Everything degrades gracefully when those are absent.
     */
    var klaviyoCfg = boot.klaviyo || {};
    var KLAVIYO_REVISION = "2024-10-15";

    function klaviyoOnsite() {
      return window.klaviyo && typeof window.klaviyo.identify === "function" ? window.klaviyo : null;
    }

    function klaviyoLegacyPush(/** @type {*} */ args) {
      try {
        window._learnq = window._learnq || [];
        window._learnq.push(args);
        return true;
      } catch (e) {
        return false;
      }
    }

    function klaviyoIdentify(/** @type {*} */ email, /** @type {*} */ extra) {
      if (!validEmail(email)) return false;
      /** @type {AnyRecord} */
      var props = { $email: sanitize(email) };
      if (extra) {
        var keys = Object.keys(extra);
        for (var i = 0; i < keys.length; i++) props[keys[i]] = extra[keys[i]];
      }
      var onsite = klaviyoOnsite();
      if (onsite) {
        try {
          onsite.identify(props);
          return true;
        } catch (e) {
          /* fall through to legacy */
        }
      }
      /* Without klaviyo.js, _learnq is never drained: save extra profile properties through the client API. */
      if (extra && sanitize(klaviyoCfg.publicKey)) {
        klaviyoClientPost("profiles", {
          data: { type: "profile", attributes: { email: sanitize(email), properties: extra } },
        });
        return true;
      }
      return klaviyoLegacyPush(["identify", props]);
    }

    function klaviyoClientPost(/** @type {*} */ path, /** @type {*} */ payload) {
      var key = sanitize(klaviyoCfg.publicKey);
      if (!key) return Promise.resolve(false);
      return fetch("https://a.klaviyo.com/client/" + path + "/?company_id=" + encodeURIComponent(key), {
        method: "POST",
        headers: { "Content-Type": "application/json", revision: KLAVIYO_REVISION },
        body: JSON.stringify(payload),
        mode: "cors",
        keepalive: true,
      })
        .then(function(/** @type {*} */ r) {
          return r.ok || r.status === 202;
        })
        .catch(function() {
          return false;
        });
    }

    function klaviyoTrack(/** @type {*} */ metric, /** @type {*} */ props) {
      var name = sanitize(metric) || "Completed Skin Quiz";
      var onsite = klaviyoOnsite();
      if (onsite && typeof onsite.track === "function") {
        try {
          var r = onsite.track(name, props || {});
          return r && typeof r.then === "function"
            ? r.then(function() { return true; }).catch(function() { return true; })
            : Promise.resolve(true);
        } catch (e) {
          /* fall through */
        }
      }
      /* No onsite object means klaviyo.js isn't loaded and nothing drains _learnq, so prefer the client API. */
      var email = props && props.$email ? props.$email : state.email;
      if (!validEmail(email) || !sanitize(klaviyoCfg.publicKey)) {
        return Promise.resolve(klaviyoLegacyPush(["track", name, props || {}]));
      }
      return klaviyoClientPost("events", {
        data: {
          type: "event",
          attributes: {
            metric: { data: { type: "metric", attributes: { name: name } } },
            properties: props || {},
            profile: { data: { type: "profile", attributes: { email: sanitize(email) } } },
          },
        },
      });
    }

    function klaviyoSubscribe(/** @type {*} */ email) {
      var listId = sanitize(klaviyoCfg.listId);
      if (!validEmail(email) || !listId || !sanitize(klaviyoCfg.publicKey)) return Promise.resolve(false);
      return klaviyoClientPost("subscriptions", {
        data: {
          type: "subscription",
          attributes: {
            custom_source: "Skin Quiz",
            profile: { data: { type: "profile", attributes: { email: sanitize(email) } } },
          },
          relationships: { list: { data: { type: "list", id: listId } } },
        },
      });
    }

    /* Email clients cannot resolve relative paths, so Klaviyo payloads carry absolute URLs. */
    function absoluteUrl(/** @type {*} */ path) {
      var value = sanitize(path).trim();
      if (!value) return "";
      if (/^https?:\/\//i.test(value)) return value;
      var origin = window.location && window.location.origin ? window.location.origin : "";
      return origin + (value.charAt(0) === "/" ? value : "/" + value);
    }

    function klaviyoProductList(/** @type {*} */ items) {
      /** @type {AnyRecord[]} */
      var out = [];
      var list = Array.isArray(items) ? items : [];
      for (var i = 0; i < list.length; i++) {
        var it = list[i];
        if (!it) continue;
        var image = sanitize(it.image || "");
        out.push({
          title: sanitize(it.title || ""),
          role: t(sanitize(it.role || it.category || "")),
          price: sanitize(it.price || ""),
          url: absoluteUrl(resolveProductUrl(it)),
          image: image && image.indexOf("//") === 0 ? "https:" + image : image,
          available: it.available !== false,
        });
      }
      return out;
    }

    function buildKlaviyoProps(/** @type {*} */ model, /** @type {*} */ email) {
      var m = model || state.result || {};
      /** @type {Record<string, number>} */
      var scores = m.radarScores || {};
      /** @type {Record<string, string[]>} */
      var actions = m.actions || {};
      var core = (m.routine && m.routine.core) || [];
      var full = buildFullItems(m);
      var origin = window.location && window.location.origin ? window.location.origin : "";
      return {
        $email: sanitize(email),
        quiz: "skin-routine",
        locale: sanitize(boot.locale || "en"),
        profile_key: sanitize(m.profileKey || ""),
        profile_title: sanitize(m.profileTitle || ""),
        tier: sanitize(m.tier || ""),
        segment: sanitize(m.segment || ""),
        scores: {
          cleansing: Number(scores.cleansing || 0),
          nutrition: Number(scores.nutrition || 0),
          hormonal_balance: Number(scores.hormonal_balance || 0),
          stress: Number(scores.stress || 0),
          moisturizing: Number(scores.moisturizing || 0),
        },
        actions_start: Array.isArray(actions.start) ? actions.start : [],
        actions_stop: Array.isArray(actions.stop) ? actions.stop : [],
        actions_keep: Array.isArray(actions.keep) ? actions.keep : [],
        core_products: klaviyoProductList(core),
        full_products: klaviyoProductList(full),
        routine_url: origin + (window.location ? window.location.pathname : ""),
        collection_url: absoluteUrl(m.fallbackCollection || ""),
        discount_core: sanitize(klaviyoCfg.coreCode || ""),
        discount_full: sanitize(klaviyoCfg.fullCode || ""),
      };
    }

    /* Lead capture at the email gate (before the report exists): profile + consent only. */
    function klaviyoCaptureLead(/** @type {*} */ email) {
      if (!validEmail(email)) return Promise.resolve(false);
      klaviyoIdentify(email);
      return klaviyoSubscribe(email);
    }

    /* Fire the full report metric once, when we have both an email and a built result. */
    function sendKlaviyoReport(/** @type {*} */ email) {
      if (!validEmail(email) || !state.result) return Promise.resolve(false);
      if (state.klaviyoReportSent) return Promise.resolve(true);
      state.klaviyoReportSent = true;
      var props = buildKlaviyoProps(state.result, email);
      klaviyoIdentify(email, { spq_profile: props.profile_key, spq_tier: props.tier, spq_segment: props.segment });
      var subscribe = klaviyoSubscribe(email);
      var track = klaviyoTrack(klaviyoCfg.metric || "Completed Skin Quiz", props);
      return Promise.all([subscribe, track]).then(function(/** @type {*} */ res) {
        return !!(res[0] || res[1]);
      });
    }

    function onReportSubmit(/** @type {*} */ event) {
      event.preventDefault();
      var email = emailReportInput ? sanitize(emailReportInput.value).trim() : "";
      if (!validEmail(email)) {
        setStatus(emailReportStatus, sanitize((boot.labels && boot.labels.reportEmailInvalid) || "Please enter a valid email address."), "error");
        emit("spq:email_report_submit_v2", { status: "invalid", email: email });
        return;
      }
      state.email = email;
      var submitBtn = emailReportForm ? emailReportForm.querySelector("button[type='submit']") : null;
      if (submitBtn) submitBtn.disabled = true;
      Promise.all([sendKlaviyoReport(email), submitReportEmail(emailReportForm)])
        .then(function(/** @type {*} */ res) {
          var ok = !!(res[0] || res[1]);
          if (ok) {
            setStatus(emailReportStatus, sanitize((boot.labels && boot.labels.reportEmailSuccess) || "Thanks. Your report is on its way."), "success");
            emit("spq:email_report_submit_v2", { status: "success", email: email });
          } else {
            setStatus(emailReportStatus, sanitize((boot.labels && boot.labels.reportEmailError) || "We could not send your report email right now. Please try again."), "error");
            emit("spq:email_report_submit_v2", { status: "failed", email: email });
          }
        })
        .finally(function() {
          if (submitBtn) submitBtn.disabled = false;
        });
    }

    function onAddAll() {
      clearStatus(addStatus);
      if (!state.result || !state.result.bundleVariantIds || !state.result.bundleVariantIds.length) {
        setStatus(addStatus, label("coreOutOfStockLabel", "All recommended core products are currently out of stock."), "error");
        return;
      }
      var result = state.result;
      var ids = result.bundleVariantIds.slice();
      setAddToCartLoadingState(addAllBtn);
      emit("spq:add_bundle_click", { profile: result.profileKey, item_count: ids.length, skipped_out_of_stock: result.bundleOutOfStockCount || 0 });
      applyDiscountCode("core")
        .then(function() {
          return addBundle(ids);
        })
        .then(function(/** @type {*} */ resource) {
          cartUpdate(resource, ids.length);
          return animateBubbleToCart(addAllBtn);
        })
        .then(function() {
          if (result.bundleOutOfStockCount > 0) {
            setStatus(
              addStatus,
              fmt(label("addAvailableOnlyLabel", "Added available items to cart. {count} recommended products are out of stock."), {
                count: result.bundleOutOfStockCount,
              }),
              "success"
            );
          } else {
            setStatus(addStatus, sanitize((boot.labels && boot.labels.addAllSuccess) || "Added to cart."), "success");
          }
          emit("spq:add_bundle_success", { profile: result.profileKey, item_count: ids.length, skipped_out_of_stock: result.bundleOutOfStockCount || 0 });
          openCart();
        })
        .catch(function(/** @type {*} */ e) {
          setStatus(addStatus, e && e.message ? e.message : label("addBundleErrorLabel", "We could not add this routine to your cart."), "error");
          emitError("add_bundle", e && e.message ? e.message : "Add bundle failed");
        })
        .finally(function() {
          var canAdd = !!(state.result && state.result.bundleVariantIds && state.result.bundleVariantIds.length);
          restoreAddToCartState(addAllBtn, addAllBtnDefaultLabel, canAdd);
        });
    }

    function onAddAllFull() {
      clearStatus(addFullStatus);
      if (!state.result || !state.result.fullBundleVariantIds || !state.result.fullBundleVariantIds.length) {
        setStatus(addFullStatus, label("fullNoProductsLabel", "No products available to add."), "error");
        return;
      }
      var result = state.result;
      var fullBundleVariantIds = Array.isArray(result.fullBundleVariantIds) ? result.fullBundleVariantIds : [];
      var ids = fullBundleVariantIds.slice();
      setAddToCartLoadingState(addAllFullBtn);
      emit("spq:add_full_bundle_click", { profile: result.profileKey, item_count: ids.length });
      applyDiscountCode("full")
        .then(function() {
          return addBundle(ids);
        })
        .then(function(/** @type {*} */ resource) {
          cartUpdate(resource, ids.length);
          return animateBubbleToCart(addAllFullBtn);
        })
        .then(function() {
          setStatus(addFullStatus, sanitize((boot.labels && boot.labels.addAllSuccess) || "Added to cart."), "success");
          emit("spq:add_full_bundle_success", { profile: result.profileKey, item_count: ids.length });
          openCart();
        })
        .catch(function(/** @type {*} */ e) {
          setStatus(addFullStatus, e && e.message ? e.message : label("addBundleErrorLabel", "We could not add this routine to your cart."), "error");
        })
        .finally(function() {
          var canAdd = !!(state.result && state.result.fullBundleVariantIds && state.result.fullBundleVariantIds.length);
          restoreAddToCartState(addAllFullBtn, addAllFullBtnDefaultLabel, canAdd);
        });
    }

    function onFallbackClick() {
      emit("spq:fallback_click_v2", {
        profile: state.result && state.result.profileKey ? state.result.profileKey : "",
        tier: state.result && state.result.tier ? state.result.tier : "",
        segment: state.result && state.result.segment ? state.result.segment : "",
      });
    }

    function reset() {
      state.index = 0;
      state.answers = {};
      state.intensity = "balanced";
      state.goalProfile = null;
      state.result = null;
      state.activeView = "core";
      state.screen = "welcome";
      state.emailHandled = false;
      state.emailCaptured = false;
      state.email = "";
      state.klaviyoReportSent = false;
      clearSavedState();
      clearStatus(emailStatus);
      if (emailReportInput) emailReportInput.value = "";
      clearStatus(emailReportStatus);
      clearStatus(addStatus);
      clearStatus(addFullStatus);
      restoreAddToCartState(addAllBtn, addAllBtnDefaultLabel, false);
      restoreAddToCartState(addAllFullBtn, addAllFullBtnDefaultLabel, false);
      if (resultSummaryEl) resultSummaryEl.innerHTML = "";
      if (reportSummaryEl) reportSummaryEl.innerHTML = "";
      if (radarEl) radarEl.innerHTML = "";
      if (strengthEl) strengthEl.innerHTML = "";
      if (actionsEl) actionsEl.innerHTML = "";
      if (resultAtGlance) resultAtGlance.innerHTML = "";
      if (resultHero) resultHero.hidden = true;
      if (resultInsight) {
        resultInsight.innerHTML = "";
        resultInsight.hidden = true;
      }
      if (resultMeta) {
        resultMeta.innerHTML = "";
        resultMeta.hidden = true;
      }
      setView("core");
      if (emailReportWrap) emailReportWrap.hidden = true;
      showScreen(welcomeScreen);
      setProgress(0, 0);
      setCounter("");
      if (backBtn) backBtn.style.display = "none";
    }

    function start() {
      emit("spq:start", { mode: "report_builder_v2", total_questions: QUESTION_ORDER.length });
      state.index = 0;
      showScreen(quizScreen);
      /* Dropdown animation on quiz start */
      if (quizScreen) {
        quizScreen.classList.add("is-dropping");
        quizScreen.addEventListener("animationend", function handler() {
          quizScreen.classList.remove("is-dropping");
          quizScreen.removeEventListener("animationend", handler);
        });
      }
      paintQuestion();
    }

    if (startBtn) startBtn.textContent = label("startLabel", "Start quiz");
    if (backBtn) {
      var backSr = backBtn.querySelector(".spq__visuallyHidden");
      if (backSr) backSr.textContent = label("backLabel", "Back");
      else backBtn.setAttribute("aria-label", label("backLabel", "Back"));
    }
    if (welcomeKicker) welcomeKicker.textContent = label("kicker", "Skin quiz");
    if (welcomeTitle) welcomeTitle.textContent = label("title", "Find your skin type");
    if (welcomeSubtitle) welcomeSubtitle.textContent = label("subtitle", "Answer 9 quick questions and get your routine in about 90 seconds.");
    if (loadingText) loadingText.textContent = label("reportBuildingText", "Building your report...");
    if (resultKicker) resultKicker.textContent = label("resultKicker", "Your skin profile");
    if (retakeHintEl) retakeHintEl.textContent = label("retakeHintLabel", "Doesn't seem right?");
    for (var t2 = 0; t2 < resultTabs.length; t2++) {
      var v = resultTabs[t2].getAttribute("data-spq-view");
      var tabLabel = resultTabs[t2].querySelector(".spq__tabLabel");
      if (!tabLabel) continue;
      if (v === "core") tabLabel.textContent = label("coreTabLabel", "3-step routine");
      if (v === "full") tabLabel.textContent = label("fullTabLabel", "Full routine");
      if (v === "optional") tabLabel.textContent = label("optionalTabLabel", "Optional extras");
    }
    if (coreSection) {
      var ct = coreSection.querySelector(".spq__routineHeading");
      if (ct) ct.textContent = label("coreRoutineHeading", "Your 3-step routine");
    }
    if (fullSection) {
      var ft = fullSection.querySelector(".spq__routineHeading");
      if (ft) ft.textContent = label("fullRoutineHeading", "Your full routine");
    }
    if (optionalSection) {
      var ot = optionalSection.querySelector(".spq__routineHeading");
      if (ot) ot.textContent = label("optionalHeading", "Optional extras");
    }

    if (emailReportWrap) emailReportWrap.hidden = true;
    if (startBtn) startBtn.addEventListener("click", start);
    if (emailForm) emailForm.addEventListener("submit", onEmailGateSubmit);
    if (emailSkipBtn) emailSkipBtn.addEventListener("click", onEmailGateSkip);
    if (backBtn) {
      backBtn.addEventListener("click", function() {
        if (state.screen === "email") {
          /* Back from the email gate returns to the last question without losing the answer. */
          showScreen(quizScreen);
          paintQuestion();
          return;
        }
        if (state.index <= 0) {
          showScreen(welcomeScreen);
          setProgress(0, 0);
          setCounter("");
          return;
        }
        state.index -= 1;
        paintQuestion();
      });
    }
    if (restartBtn) restartBtn.addEventListener("click", reset);
    if (addAllBtn) addAllBtn.addEventListener("click", onAddAll);
    if (addAllFullBtn) addAllFullBtn.addEventListener("click", onAddAllFull);
    if (fallbackBtn) fallbackBtn.addEventListener("click", onFallbackClick);
    if (emailReportForm) emailReportForm.addEventListener("submit", onReportSubmit);
    for (var x = 0; x < resultTabs.length; x++) {
      (function(/** @type {*} */ tab) {
        tab.addEventListener("click", function() {
          if (tab.disabled) return;
          setView(tab.getAttribute("data-spq-view") || "core");
        });
        tab.addEventListener("keydown", function(/** @type {*} */ event) {
          if (!event || tab.disabled) return;
          var key = event.key;
          var tabs = /** @type {HTMLElement[]} */ (Array.prototype.slice.call(resultTabs));
          var currentIndex = tabs.indexOf(tab);
          if (currentIndex < 0) return;
          var nextIndex = currentIndex;
          if (key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
          else if (key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          else if (key === "Home") nextIndex = 0;
          else if (key === "End") nextIndex = tabs.length - 1;
          else if (key === "Enter" || key === " " || key === "Spacebar") {
            event.preventDefault();
            setView(tab.getAttribute("data-spq-view") || "core");
            return;
          } else return;
          event.preventDefault();
          var targetTab = tabs[nextIndex];
          if (!targetTab || targetTab.disabled) return;
          setView(targetTab.getAttribute("data-spq-view") || "core");
          targetTab.focus();
        });
      })(resultTabs[x]);
    }

    if (!restoreState()) {
      showScreen(welcomeScreen);
      setProgress(0, 0);
      setCounter("");
      if (backBtn) backBtn.style.display = "none";
      saveState();
    }
  }

  /* 06) Global bootstrap */
  /* Test hook: lets plain Node scripts drive the pure scoring core (see scripts/). */
  window.__SPQ_TEST__ = {
    ensureQuestionMaps: ensureQuestionMaps,
    QUESTIONS: QUESTIONS,
    QUESTION_ORDER: QUESTION_ORDER,
    RADAR_SOURCES: RADAR_SOURCES,
    SPQ_ICON_MAP: SPQ_ICON_MAP,
    AXES: AXES,
    computeProfileScores: computeProfileScores,
    chooseProfileFrom: chooseProfileFrom,
    computeRadarScores: computeRadarScores,
  };

  function initAll() {
    ensureQuestionMaps();
    var sections = document.querySelectorAll(".spq[data-section-id]");
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initAll);
  else initAll();
})();


